// OralExamVoicePage.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useOralVoiceStartup, MICROPHONE_TIMEOUT_MS, CONNECTION_TIMEOUT_MS } from "./oralVoiceStartup";
import VoiceStartupRecovery from "./VoiceStartupRecovery";
import PublicAccountSummary from "./PublicAccountSummary";
import { PUBLIC_SESSION_CONFIG, getPublicSession, getPublicSessionPayload, isPublicSessionError, logoutPublicSession } from "./publicAccountSession";
import {
  OpenAIRealtimeWebRTC,
  RealtimeAgent,
  RealtimeSession,
  tool,
} from "@openai/agents/realtime";
import { z } from "zod";
import "./OralExamVoicePage.css";

const cardStyle = {
  background: "#ffffff",
  border: "1px solid #e5e7eb",
  borderRadius: 12,
  padding: 16,
  boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
};

const labelStyle = {
  fontWeight: 700,
  marginBottom: 6,
  display: "block",
};

const inputStyle = {
  width: "100%",
  padding: "10px 12px",
  borderRadius: 8,
  border: "1px solid #d1d5db",
  fontSize: 14,
  boxSizing: "border-box",
};

const buttonStyle = {
  padding: "10px 16px",
  borderRadius: 8,
  border: "none",
  cursor: "pointer",
  fontWeight: 700,
};

const secondaryButtonStyle = {
  ...buttonStyle,
  background: "#e5e7eb",
  color: "#111827",
};

const primaryButtonStyle = {
  ...buttonStyle,
  background: "#3F51B5",
  color: "#ffffff",
};

const dangerButtonStyle = {
  ...buttonStyle,
  background: "#b42318",
  color: "#ffffff",
};

const ORAL_DEBUG =
  window.location.hostname === "sandbox-schools.articulate.com.sg";
const POST_ASSISTANT_AUDIO_MIC_BUFFER_MS = 250;
const COMPLETION_CLEANUP_FALLBACK_MS = 18000;
const PRACTICE_TYPE = "dsa_interview";
const DSA_LEVEL_ID = "primary_dsa";
const DSA_DEFAULT_CONTEXT = {
  school_id: "public_use",
  teacher_id: "public_oral",
  year: String(new Date().getFullYear()),
  student_class: "public_usage",
  language_id: "english",
};
const DSA_FALLBACK_QUESTION_SETS = [
  {
    topic_set_id: "personal_profile_1",
    topic_set_name: "Personal Profile 1",
    category_name: "Personal Profile",
  },
  {
    topic_set_id: "personal_profile_2",
    topic_set_name: "Personal Profile 2",
    category_name: "Personal Profile",
  },
  {
    topic_set_id: "talent_development_1",
    topic_set_name: "Talent Development 1",
    category_name: "Talent Development",
  },
  {
    topic_set_id: "talent_development_2",
    topic_set_name: "Talent Development 2",
    category_name: "Talent Development",
  },
  {
    topic_set_id: "school_alignment_1",
    topic_set_name: "School Alignment 1",
    category_name: "School Alignment",
  },
  {
    topic_set_id: "school_alignment_2",
    topic_set_name: "School Alignment 2",
    category_name: "School Alignment",
  },
  {
    topic_set_id: "character_attributes_1",
    topic_set_name: "Character Attributes 1",
    category_name: "Character Attributes",
  },
  {
    topic_set_id: "character_attributes_2",
    topic_set_name: "Character Attributes 2",
    category_name: "Character Attributes",
  },
  {
    topic_set_id: "future_readiness_1",
    topic_set_name: "Future Readiness 1",
    category_name: "Future Readiness",
  },
  {
    topic_set_id: "future_readiness_2",
    topic_set_name: "Future Readiness 2",
    category_name: "Future Readiness",
  },
];
const PAUSE_DURATION_OPTIONS = [
  { value: "fast", label: "Short" },
  { value: "normal", label: "Normal (Recommended)" },
  { value: "patient", label: "Extended" },
];

const LISTENING_STATE_CONFIG = {
  teacher_speaking: {
    label: "Interviewer is speaking — please wait",
    border: "#eab308",
    background: "#fef9c3",
    text: "#713f12",
  },
  muted: {
    label: "Mic opening — wait for green",
    border: "#f97316",
    background: "#ffedd5",
    text: "#9a3412",
  },
  ready: {
    label: "Ready — answer now",
    border: "#22c55e",
    background: "#dcfce7",
    text: "#166534",
  },
  thinking: {
    label: "Thinking time",
    border: "#6366f1",
    background: "#eef2ff",
    text: "#3730a3",
  },
  processing: {
    label: "Processing answer",
    border: "#9ca3af",
    background: "#f3f4f6",
    text: "#374151",
  },
  paused: {
    label: "Paused — take your time",
    border: "#6366f1",
    background: "#eef2ff",
    text: "#3730a3",
  },
};

const DEFAULT_LANGUAGES = [
  { language_id: "english", language_name: "English" },
];

function getSafeLanguages(payload) {
  return Array.isArray(payload?.languages) && payload.languages.length > 0
    ? payload.languages
    : DEFAULT_LANGUAGES;
}

function getSafeLanguageId(language) {
  return language?.language_id || "english";
}

function getDefaultLanguageId(payload, fallbackLanguageId = "english") {
  const safeLanguages = getSafeLanguages(payload);
  const firstLanguageId = getSafeLanguageId(safeLanguages[0]);
  const payloadLanguageId = payload?.language_id || "";
  const payloadLanguageIsSupported = safeLanguages.some(
    (language) => getSafeLanguageId(language) === payloadLanguageId
  );

  return (
    (payloadLanguageIsSupported && payloadLanguageId) ||
    firstLanguageId ||
    fallbackLanguageId ||
    "english"
  );
}

function mapBackendTurnDetectionToSdk(turnDetection = {}) {
  return {
    type: turnDetection.type || "server_vad",
    threshold: Number(turnDetection.threshold ?? 0.7),
    prefixPaddingMs: Number(
      turnDetection.prefix_padding_ms ?? turnDetection.prefixPaddingMs ?? 1500
    ),
    silenceDurationMs: Number(
      turnDetection.silence_duration_ms ??
        turnDetection.silenceDurationMs ??
        1300
    ),
    createResponse: Boolean(
      turnDetection.create_response ?? turnDetection.createResponse ?? false
    ),
    interruptResponse: Boolean(
      turnDetection.interrupt_response ??
        turnDetection.interruptResponse ??
        false
    ),
  };
}

function getTopicStimulusMedia(topic, sessionInfo) {
  const media = topic?.stimulus_media || sessionInfo?.stimulusMedia || null;

  if (media?.media_type) {
    return {
      mediaType: media.media_type || "none",
      sourceType: media.source_type || "",
      displayUrl: media.display_url || "",
      videoUrl: media.video_url || "",
      description:
        media.description ||
        topic?.picture_stimulus ||
        sessionInfo?.pictureStimulus ||
        "",
    };
  }

  if (topic?.picture_stimulus_url || sessionInfo?.pictureStimulusUrl) {
    return {
      mediaType: "image",
      sourceType: "legacy_picture_url",
      displayUrl:
        topic?.picture_stimulus_url || sessionInfo?.pictureStimulusUrl || "",
      videoUrl: "",
      description:
        topic?.picture_stimulus ||
        sessionInfo?.pictureStimulus ||
        "",
    };
  }

  return {
    mediaType: "none",
    sourceType: "",
    displayUrl: "",
    videoUrl: "",
    description:
      topic?.picture_stimulus ||
      sessionInfo?.pictureStimulus ||
      "",
  };
}

function getOrderedQuestions(questions) {
  return Object.entries(questions || {})
    .map(([key, text]) => {
      const match = /^question_(\d+)$/.exec(key);
      if (!match) {
        return null;
      }

      const trimmedText = String(text || "").trim();
      if (!trimmedText) {
        return null;
      }

      return {
        key,
        index: Number(match[1]),
        text: trimmedText,
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.index - b.index);
}

function getQuestionSetId(questionSet) {
  return (
    questionSet?.set_id ||
    questionSet?.question_set_id ||
    questionSet?.topic_set_id ||
    questionSet?.id ||
    ""
  );
}

function getQuestionSetName(questionSet) {
  return (
    questionSet?.set_name ||
    questionSet?.question_set_name ||
    questionSet?.topic_set_name ||
    questionSet?.name ||
    getQuestionSetId(questionSet)
  );
}

function getQuestionSetQuestions(questionSet) {
  const questions = questionSet?.questions || questionSet?.items || [];

  if (Array.isArray(questions)) {
    return questions.reduce((acc, question, index) => {
      acc[`question_${index + 1}`] =
        typeof question === "string"
          ? question
          : question?.question ||
            question?.question_text ||
            question?.text ||
            question?.prompt ||
            "";
      return acc;
    }, {});
  }

  return questions || {};
}

function getQuestionSetQuestionCount(questionSet) {
  const explicitCount =
    questionSet?.question_count ||
    questionSet?.questions_count ||
    questionSet?.number_of_questions;

  if (explicitCount) {
    return explicitCount;
  }

  return getOrderedQuestions(getQuestionSetQuestions(questionSet)).length;
}

function normalizeQuestionSets(payload) {
  const rawQuestionSets =
    payload?.question_sets ||
    payload?.practice_sets ||
    payload?.topic_sets ||
    [];
  const source =
    Array.isArray(rawQuestionSets) && rawQuestionSets.length > 0
      ? rawQuestionSets
      : DSA_FALLBACK_QUESTION_SETS;

  return source
    .map((questionSet) => {
      const id = getQuestionSetId(questionSet);
      const name = getQuestionSetName(questionSet);

      if (!id) {
        return null;
      }

      return {
        ...questionSet,
        question_set_id: id,
        question_set_name: name,
        topic_set_id: id,
        topic_set_name: name,
        category_name:
          questionSet?.category_name ||
          questionSet?.category ||
          questionSet?.section_name ||
          "",
        estimated_duration:
          questionSet?.estimated_duration ||
          questionSet?.estimated_duration_label ||
          questionSet?.duration ||
          "",
        questions: getQuestionSetQuestions(questionSet),
      };
    })
    .filter(Boolean);
}

export function isDsaReportPublicSessionError(status, payload) {
  return isPublicSessionError({ response: { status, data: payload } });
}

export async function refreshDsaPublicBalances(sessionGetter = getPublicSession) {
  const session = getPublicSessionPayload(await sessionGetter());
  return session.authenticated ? session.balances : null;
}

function DsaInterviewVoicePage() {
  const [checkingPublicSession, setCheckingPublicSession] = useState(true);
  const [publicSessionError, setPublicSessionError] = useState("");
  const [publicUser, setPublicUser] = useState(null);
  const [publicBalances, setPublicBalances] = useState(null);
  const [loggingOut, setLoggingOut] = useState(false);
  const [topicsData, setTopicsData] = useState(null);
  const [loadingTopics, setLoadingTopics] = useState(false);
  const [topicsError, setTopicsError] = useState("");

  const [studentName, setStudentName] = useState("");
  const [studentRegNumber, setStudentRegNumber] = useState("");
  const [publicEmailCode, setPublicEmailCode] = useState("");
  const [emailCodeSent, setEmailCodeSent] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  const [emailVerificationStatus, setEmailVerificationStatus] = useState("");
  const [sendingEmailCode, setSendingEmailCode] = useState(false);
  const [verifyingEmailCode, setVerifyingEmailCode] = useState(false);
  const [userId, setUserId] = useState("");
  const [resolvedClassContext] = useState(DSA_DEFAULT_CONTEXT);
  const verificationStatus = "Choose a DSA interview practice set.";

  const clearPublicAuthentication = (message = "") => {
    setPublicUser(null); setPublicBalances(null); setEmailVerified(false);
    setUserId(""); setStudentRegNumber(""); setPublicEmailCode(""); setEmailCodeSent(false);
    setTopicsData(null); setEmailVerificationStatus(message);
  };

  const checkPublicSession = async () => {
    setCheckingPublicSession(true); setPublicSessionError("");
    try {
      const session = getPublicSessionPayload(await getPublicSession());
      if (session.authenticated) {
        setPublicUser(session.user); setPublicBalances(session.balances);
        setUserId(session.user?.user_id || "");
        setStudentRegNumber(session.user?.masked_email || "");
        setEmailVerified(Boolean(session.user?.user_id));
        setEmailVerificationStatus("Account session restored.");
      } else clearPublicAuthentication();
    } catch (_) { setPublicSessionError("We could not check your account. Please try again."); }
    finally { setCheckingPublicSession(false); }
  };
  const [selectedLanguageId, setSelectedLanguageId] = useState("english");
  const [selectedStandard, setSelectedStandard] = useState(DSA_LEVEL_ID);
  const [selectedTopicSetId, setSelectedTopicSetId] = useState("");
  const [selectedTopicId, setSelectedTopicId] = useState("");
  const [selectedPauseDurationMode, setSelectedPauseDurationMode] =
    useState("normal");

  const voiceStartup = useOralVoiceStartup();
  const startRequestPendingRef = useRef(false);
  const voiceListenersCleanupRef = useRef(() => {});
  const [startingSession, setStartingSession] = useState(false);
  const voiceStartupLocked = startingSession || voiceStartup.busy || !!voiceStartup.error;
  const [sessionStatus, setSessionStatus] = useState("Not started");
  const [listeningState, setListeningState] = useState("muted");
  const [studentCanRespond, setStudentCanRespond] = useState(false);
  const [waitingForReadyPress, setWaitingForReadyPress] = useState(false);
  const [readyButtonPressedAt, setReadyButtonPressedAt] = useState("");
  const [ignoredTranscriptBeforeReady, setIgnoredTranscriptBeforeReady] =
    useState(0);
  const [isConnected, setIsConnected] = useState(false);
  const [realtimeCleanupInProgress, setRealtimeCleanupInProgress] =
    useState(false);
  const [reportUrl, setReportUrl] = useState("");
  const [reportStatus, setReportStatus] = useState("");

  const [sessionInfo, setSessionInfo] = useState({
    sessionId: "",
    status: "",
    languageId: "",
    standard: "",
    topicSetId: "",
    topicId: "",
    topicTitle: "",
    pictureStimulus: "",
    pictureStimulusUrl: "",
    stimulusMedia: {
      media_type: "",
      source_type: "",
      display_url: "",
      video_url: "",
      blob_name: "",
      description: "",
    },
    questions: {},
    questionCount: 0,
    currentQuestionIndex: 1,
    spokenPrompt: "",
  });

  const [eventLog, setEventLog] = useState([]);

  const sessionRef = useRef(null);
  const microphoneStreamRef = useRef(null);
  const intentionalCloseRef = useRef(false);
  const startupGuardRef = useRef(false);
  const firstTutorUtteranceDoneRef = useRef(false);
  const startupUnlockTimerRef = useRef(null);
  // Deprecated: do not use old duration-based mute timing to control mic mute.
  // Mic gating is driven by realtime audio_start/audio_stopped output events.
  const promptMuteTimerRef = useRef(null);
  const completionCloseTimerRef = useRef(null);
  const completionCleanupFallbackTimerRef = useRef(null);
  const activeSessionKeyRef = useRef("");
  const completionCleanupSessionKeyRef = useRef("");
  const completionCleanupStartedRef = useRef(false);
  const micMutedRef = useRef(false);
  const realtimeResponseActiveRef = useRef(false);
  const realtimeResponseRequestPendingRef = useRef(false);
  const realtimeResponseRequestSourceRef = useRef("");
  const activeRealtimeResponseIdRef = useRef("");
  const assistantAudioActiveRef = useRef(false);
  const responseActiveDiagnosticTimerRef = useRef(null);
  const isPracticePausedRef = useRef(false);
  const prePauseListeningStateRef = useRef("ready");
  const listeningStateRef = useRef("muted");
  const sessionStatusRef = useRef("Not started");
  const studentCanRespondRef = useRef(false);
  const studentReadyToAnswerRef = useRef(false);
  const waitingForReadyPressRef = useRef(false);
  const pendingTutorPromptActionRef = useRef("next_question");
  const readyButtonPressedAtRef = useRef("");
  const ignoredTranscriptBeforeReadyRef = useRef(0);
  const frontendTurnIdRef = useRef(0);
  const lastAudioGateReleasedTurnIdRef = useRef(null);
  const oralDebugTimelineRef = useRef({
    promptStartedAt: "",
    promptFinishedAt: "",
    answerCaptureReadyAt: "",
    firstTranscriptDeltaAt: "",
    firstFinalTranscriptAt: "",
    transcriptBufferLength: 0,
    transcriptPreview: "",
    promptFinishedTurnId: null,
    answerCaptureReadyTurnId: null,
    readyButtonPressedAt: "",
    ignoredTranscriptBeforeReady: 0,
  });
  const sessionMetaRef = useRef({
    sessionId: "",
    currentQuestionIndex: 1,
    userEmail: "",
    userId: "",
    lastSpokenPrompt: "",
    closed: false,
  });

  const debugLog = (label, payload = {}) => {
    if (!ORAL_DEBUG) {
      return;
    }

    console.log(label, {
      timestamp: new Date().toISOString(),
      currentQuestionIndex: sessionMetaRef.current.currentQuestionIndex || 1,
      micMuted: micMutedRef.current,
      ...payload,
    });
  };

  const getOralDebugSnapshot = (overrides = {}) => {
    const timeline = oralDebugTimelineRef.current;
    const transcript =
      overrides.transcript !== undefined
        ? String(overrides.transcript || "")
        : timeline.transcriptPreview || "";
    const transcriptPreview =
      overrides.transcriptPreview !== undefined
        ? String(overrides.transcriptPreview || "").slice(0, 200)
        : transcript.slice(0, 200);

    return {
      timestamp: new Date().toISOString(),
      session_id: sessionMetaRef.current.sessionId || "",
      currentQuestionIndex: sessionMetaRef.current.currentQuestionIndex || 1,
      frontendTurnId: frontendTurnIdRef.current,
      status: listeningStateRef.current || sessionStatusRef.current || "",
      micMuted: micMutedRef.current,
      answerCaptureReady: Boolean(studentCanRespondRef.current),
      studentReadyToAnswer: Boolean(studentReadyToAnswerRef.current),
      studentCanRespond: Boolean(studentCanRespondRef.current),
      waitingForReadyPress: Boolean(waitingForReadyPressRef.current),
      realtimeResponseActive: Boolean(realtimeResponseActiveRef.current),
      studentReadyToAnswerRefCurrent: Boolean(studentReadyToAnswerRef.current),
      studentCanRespondRefCurrent: Boolean(studentCanRespondRef.current),
      realtimeResponseActiveRefCurrent: Boolean(
        realtimeResponseActiveRef.current
      ),
      readyButtonPressedAt:
        overrides.readyButtonPressedAt !== undefined
          ? overrides.readyButtonPressedAt
          : readyButtonPressedAtRef.current || timeline.readyButtonPressedAt || "",
      ignoredTranscriptBeforeReady:
        overrides.ignoredTranscriptBeforeReady !== undefined
          ? overrides.ignoredTranscriptBeforeReady
          : ignoredTranscriptBeforeReadyRef.current ||
            timeline.ignoredTranscriptBeforeReady ||
            0,
      promptStartedAt: timeline.promptStartedAt || "",
      promptFinishedAt: timeline.promptFinishedAt || "",
      answerCaptureReadyAt: timeline.answerCaptureReadyAt || "",
      firstTranscriptDeltaAt: timeline.firstTranscriptDeltaAt || "",
      firstFinalTranscriptAt: timeline.firstFinalTranscriptAt || "",
      transcriptBufferLength:
        overrides.transcriptBufferLength !== undefined
          ? overrides.transcriptBufferLength
          : timeline.transcriptBufferLength || transcript.length || 0,
      transcriptPreview,
      reason: overrides.reason || "",
      ...overrides,
    };
  };

  const oralVoiceDebugLog = (label, payload = {}) => {
    if (!ORAL_DEBUG) {
      return;
    }

    console.log(label, getOralDebugSnapshot(payload));
  };

  const oralVoiceDebugWarn = (label, payload = {}) => {
    if (!ORAL_DEBUG) {
      return;
    }

    console.warn(label, getOralDebugSnapshot(payload));
  };

  const setMicrophoneTrackEnabled = (enabled) => {
    const stream = microphoneStreamRef.current;
    if (!stream) {
      micMutedRef.current = !enabled;
      return 0;
    }

    const audioTracks = stream.getAudioTracks();
    audioTracks.forEach((track) => {
      track.enabled = enabled;
    });
    micMutedRef.current = !enabled;
    return audioTracks.length;
  };

  const setWaitingForReadyPressValue = (value) => {
    waitingForReadyPressRef.current = value;
    setWaitingForReadyPress(value);
  };

  const disableMicrophoneTrack = (reason) => {
    const trackCount = setMicrophoneTrackEnabled(false);
    debugLog(
      "[ORAL DEBUG] Assistant audio started: mic disabled",
      getOralDebugSnapshot({ reason, trackCount })
    );
  };

  const enableMicrophoneAfterAudioBuffer = (reason) => {
    const trackCount = setMicrophoneTrackEnabled(true);
    debugLog(
      "[ORAL DEBUG] Mic enabled after 250ms buffer",
      getOralDebugSnapshot({
        reason,
        delayMs: POST_ASSISTANT_AUDIO_MIC_BUFFER_MS,
        trackCount,
      })
    );
  };

  const enableMicrophoneForReadyPress = (reason) => {
    const trackCount = setMicrophoneTrackEnabled(true);
    debugLog(
      "[ORAL DEBUG] Ready pressed: mic enabled",
      getOralDebugSnapshot({ reason, trackCount })
    );
  };

  const markTranscriptBufferReset = (reason) => {
    const timeline = oralDebugTimelineRef.current;
    if (timeline.firstTranscriptDeltaAt) {
      oralVoiceDebugWarn("[ORAL VOICE DEBUG WARNING] Transcript reset after first transcript delta", {
        reason,
      });
    }

    oralDebugTimelineRef.current = {
      ...timeline,
      firstTranscriptDeltaAt: "",
      firstFinalTranscriptAt: "",
      transcriptBufferLength: 0,
      transcriptPreview: "",
      readyButtonPressedAt: readyButtonPressedAtRef.current || "",
      ignoredTranscriptBeforeReady: ignoredTranscriptBeforeReadyRef.current || 0,
    };

    oralVoiceDebugLog("[ORAL VOICE DEBUG] Transcript buffer clear/reset", {
      reason,
    });
  };

  const markPromptStarted = (reason, promptText = "") => {
    frontendTurnIdRef.current += 1;
    lastAudioGateReleasedTurnIdRef.current = null;
    oralDebugTimelineRef.current = {
      promptStartedAt: new Date().toISOString(),
      promptFinishedAt: "",
      answerCaptureReadyAt: "",
      firstTranscriptDeltaAt: "",
      firstFinalTranscriptAt: "",
      transcriptBufferLength: 0,
      transcriptPreview: String(promptText || "").slice(0, 200),
      promptFinishedTurnId: null,
      answerCaptureReadyTurnId: null,
      readyButtonPressedAt: readyButtonPressedAtRef.current || "",
      ignoredTranscriptBeforeReady: ignoredTranscriptBeforeReadyRef.current || 0,
    };

    oralVoiceDebugLog("[ORAL VOICE DEBUG] Realtime AI response start", {
      reason,
      transcript: promptText,
      transcriptBufferLength: 0,
    });
    markTranscriptBufferReset(reason);
  };

  const markPromptFinished = (reason, turnId = frontendTurnIdRef.current) => {
    const timeline = oralDebugTimelineRef.current;
    const now = new Date().toISOString();

    if (timeline.promptFinishedTurnId === turnId) {
      oralVoiceDebugWarn("[ORAL VOICE DEBUG WARNING] Duplicate prompt finished event", {
        reason,
      });
    }

    oralDebugTimelineRef.current = {
      ...timeline,
      promptFinishedAt: timeline.promptFinishedAt || now,
      promptFinishedTurnId: turnId,
    };

    oralVoiceDebugLog("[ORAL VOICE DEBUG] Realtime AI response done / prompt finished", {
      reason,
      duplicatePromptFinished:
        timeline.promptFinishedTurnId === turnId,
    });
  };

  const markAnswerCaptureReady = (reason, turnId = frontendTurnIdRef.current) => {
    const timeline = oralDebugTimelineRef.current;
    const now = new Date().toISOString();

    if (timeline.answerCaptureReadyTurnId === turnId) {
      oralVoiceDebugWarn("[ORAL VOICE DEBUG WARNING] Duplicate mic-enabled / answerCaptureReady event", {
        reason,
      });
    }

    oralDebugTimelineRef.current = {
      ...timeline,
      answerCaptureReadyAt: timeline.answerCaptureReadyAt || now,
      answerCaptureReadyTurnId: turnId,
    };

    if (
      timeline.firstTranscriptDeltaAt &&
      (timeline.answerCaptureReadyAt || now) > timeline.firstTranscriptDeltaAt
    ) {
      oralVoiceDebugWarn("[ORAL VOICE DEBUG WARNING] Answer capture ready after first transcript delta", {
        reason,
      });
    }

    oralVoiceDebugLog("[ORAL VOICE DEBUG] Answer capture ready / mic enabled", {
      reason,
    });
  };

  const setThinkingAfterTutorPrompt = (reason) => {
    setMicrophoneTrackEnabled(false);
    studentReadyToAnswerRef.current = false;
    studentCanRespondRef.current = false;
    setWaitingForReadyPressValue(true);
    listeningStateRef.current = "thinking";
    sessionStatusRef.current =
      "Take your time. Tap Ready to Answer when prepared.";
    setStudentCanRespond(false);
    setListeningState("thinking");
    setSessionStatus("Take your time. Tap Ready to Answer when prepared.");
    debugLog("[ORAL DEBUG] Student thinking time started", {
      reason,
    });
  };

  const setReadyAfterTutorPrompt = (reason, turnId = frontendTurnIdRef.current) => {
    enableMicrophoneAfterAudioBuffer(reason);
    studentReadyToAnswerRef.current = true;
    studentCanRespondRef.current = true;
    setWaitingForReadyPressValue(false);
    listeningStateRef.current = "ready";
    sessionStatusRef.current = "Listening \u2014 please answer now";
    setStudentCanRespond(true);
    setListeningState("ready");
    setSessionStatus("Listening \u2014 please answer now");
    markAnswerCaptureReady(reason, turnId);
  };

  const completeTutorPromptResponse = (reason, turnId = frontendTurnIdRef.current) => {
    const promptAction = pendingTutorPromptActionRef.current || "next_question";

    if (sessionMetaRef.current.closed || promptAction === "completed") {
      studentReadyToAnswerRef.current = false;
      studentCanRespondRef.current = false;
      setWaitingForReadyPressValue(false);
      setStudentCanRespond(false);
      return;
    }

    if (promptAction === "followup") {
      setReadyAfterTutorPrompt(`${reason}: followup`, turnId);
      return;
    }

    setThinkingAfterTutorPrompt(`${reason}: ${promptAction}`);
  };

  const handleReadyToAnswer = () => {
    if (!isConnected || sessionMetaRef.current.closed) {
      return;
    }

    const pressedAt = new Date().toISOString();
    markTranscriptBufferReset("student pressed Ready to Answer");
    enableMicrophoneForReadyPress("student pressed Ready to Answer");
    studentReadyToAnswerRef.current = true;
    studentCanRespondRef.current = true;
    setWaitingForReadyPressValue(false);
    readyButtonPressedAtRef.current = pressedAt;
    oralDebugTimelineRef.current = {
      ...oralDebugTimelineRef.current,
      readyButtonPressedAt: pressedAt,
    };
    listeningStateRef.current = "ready";
    sessionStatusRef.current = "Listening \u2014 please answer now";
    setReadyButtonPressedAt(pressedAt);
    setStudentCanRespond(true);
    setListeningState("ready");
    setSessionStatus("Listening \u2014 please answer now");
    markAnswerCaptureReady("student pressed Ready to Answer");
  };

  const markFirstTranscriptDelta = (reason, transcriptText = "") => {
    const timeline = oralDebugTimelineRef.current;
    const now = new Date().toISOString();
    const nextTimeline = {
      ...timeline,
      firstTranscriptDeltaAt: timeline.firstTranscriptDeltaAt || now,
      transcriptBufferLength: String(transcriptText || "").length,
      transcriptPreview: String(transcriptText || "").slice(0, 200),
    };

    oralDebugTimelineRef.current = nextTimeline;

    if (!timeline.firstTranscriptDeltaAt) {
      oralVoiceDebugLog("[ORAL VOICE DEBUG] First transcript delta after prompt finished", {
        reason,
        transcript: transcriptText,
      });
    }

    if (nextTimeline.promptFinishedAt && nextTimeline.firstTranscriptDeltaAt < nextTimeline.promptFinishedAt) {
      oralVoiceDebugWarn("[ORAL VOICE DEBUG WARNING] First transcript delta before prompt finished", {
        reason,
        transcript: transcriptText,
      });
    }

    if (!studentCanRespondRef.current) {
      oralVoiceDebugWarn("[ORAL VOICE DEBUG WARNING] Transcript delta received before answerCaptureReady", {
        reason,
        transcript: transcriptText,
      });
    }
  };

  const markFirstFinalTranscript = (reason, transcriptText = "") => {
    const timeline = oralDebugTimelineRef.current;
    const now = new Date().toISOString();

    oralDebugTimelineRef.current = {
      ...timeline,
      firstFinalTranscriptAt: timeline.firstFinalTranscriptAt || now,
      transcriptBufferLength: String(transcriptText || "").length,
      transcriptPreview: String(transcriptText || "").slice(0, 200),
    };

    if (!timeline.firstFinalTranscriptAt) {
      oralVoiceDebugLog("[ORAL VOICE DEBUG] First final transcript after prompt finished", {
        reason,
        transcript: transcriptText,
      });
    }

    if (!studentCanRespondRef.current) {
      oralVoiceDebugWarn("[ORAL VOICE DEBUG WARNING] Final transcript received before answerCaptureReady", {
        reason,
        transcript: transcriptText,
      });
    }
  };

  useEffect(() => {
    listeningStateRef.current = listeningState;
  }, [listeningState]);

  useEffect(() => {
    sessionStatusRef.current = sessionStatus;
  }, [sessionStatus]);

  useEffect(() => {
    studentCanRespondRef.current = studentCanRespond;
  }, [studentCanRespond]);

  const normalizeRealtimeError = (err = {}) => ({
    code:
      err?.error?.error?.code ||
      err?.error?.code ||
      err?.code ||
      err?.payload?.error?.error?.code ||
      err?.payload?.error?.code ||
      "",
    message:
      err?.error?.error?.message ||
      err?.error?.message ||
      err?.message ||
      err?.payload?.error?.error?.message ||
      err?.payload?.error?.message ||
      "",
  });

  const clearResponseActiveDiagnosticTimer = () => {
    if (responseActiveDiagnosticTimerRef.current) {
      clearTimeout(responseActiveDiagnosticTimerRef.current);
      responseActiveDiagnosticTimerRef.current = null;
    }
  };

  const startResponseActiveDiagnosticTimer = (source, sessionKey) => {
    clearResponseActiveDiagnosticTimer();
    responseActiveDiagnosticTimerRef.current = setTimeout(() => {
      if (
        sessionKey === activeSessionKeyRef.current &&
        (realtimeResponseRequestPendingRef.current ||
          realtimeResponseActiveRef.current ||
          activeRealtimeResponseIdRef.current ||
          assistantAudioActiveRef.current)
      ) {
        oralVoiceDebugWarn("[ORAL VOICE DEBUG WARNING] Realtime response still active", {
          source,
          sessionKey,
          responseRequestPending: realtimeResponseRequestPendingRef.current,
          activeResponseId: activeRealtimeResponseIdRef.current,
          assistantAudioActive: assistantAudioActiveRef.current,
          pendingPromptAction: pendingTutorPromptActionRef.current,
        });
      }
    }, 20000);
  };

  const maybeClearRealtimeResponseActive = (reason) => {
    if (
      !realtimeResponseRequestPendingRef.current &&
      !activeRealtimeResponseIdRef.current &&
      !assistantAudioActiveRef.current
    ) {
      realtimeResponseActiveRef.current = false;
      clearResponseActiveDiagnosticTimer();
      debugLog("[ORAL DEBUG] Realtime response active cleared", {
        ...getOralDebugSnapshot({ reason }),
        activeResponseId: activeRealtimeResponseIdRef.current,
        assistantAudioActive: assistantAudioActiveRef.current,
      });
    }
  };

  const sendTutorRealtimeMessage = (
    realtimeSession,
    promptText,
    source = "startup_force_prompt",
    sessionKey = activeSessionKeyRef.current
  ) => {
    const debugPayload = {
      source,
      sessionKey,
      frontendTurnId: frontendTurnIdRef.current,
      currentQuestionIndex: sessionMetaRef.current.currentQuestionIndex || 1,
      responseRequestPending: realtimeResponseRequestPendingRef.current,
      realtimeResponseActive: realtimeResponseActiveRef.current,
      activeResponseId: activeRealtimeResponseIdRef.current,
      assistantAudioActive: assistantAudioActiveRef.current,
      pendingPromptAction: pendingTutorPromptActionRef.current,
    };
    const blockedReason =
      sessionKey !== activeSessionKeyRef.current
        ? "stale session key"
        : sessionMetaRef.current.closed
        ? "session completed"
        : realtimeResponseRequestPendingRef.current
        ? "response request pending"
        : realtimeResponseActiveRef.current
        ? "response active"
        : activeRealtimeResponseIdRef.current
        ? "active response id present"
        : assistantAudioActiveRef.current
        ? "assistant audio active"
        : "";

    if (blockedReason) {
      debugLog("[ORAL DEBUG] Blocked realtime response request", {
        ...debugPayload,
        blockedReason,
        promptText,
      });
      oralVoiceDebugWarn("[ORAL VOICE DEBUG WARNING] Duplicate realtime AI response start skipped", {
        ...debugPayload,
        reason: blockedReason,
        transcript: promptText,
      });
      return;
    }

    markPromptStarted("sendTutorRealtimeMessage", promptText);
    realtimeResponseRequestPendingRef.current = true;
    realtimeResponseRequestSourceRef.current = source;
    realtimeResponseActiveRef.current = true;
    studentReadyToAnswerRef.current = false;
    studentCanRespondRef.current = false;
    setWaitingForReadyPressValue(false);
    listeningStateRef.current = "teacher_speaking";
    setListeningState("teacher_speaking");
    debugLog("[ORAL DEBUG] Realtime response active set true", {
      ...debugPayload,
      promptText,
    });
    startResponseActiveDiagnosticTimer(source, sessionKey);

    realtimeSession.sendMessage(promptText);
  };

  const appendLog = (speaker, text) => {
    if (!text) return;
    setEventLog((prev) => [
      ...prev,
      {
        id: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        speaker,
        text,
        at: new Date().toLocaleTimeString(),
      },
    ]);
  };

  // eslint-disable-next-line no-unused-vars
  const resumePractice = () => {
    if (!isConnected || !isPracticePausedRef.current) {
      return;
    }

    isPracticePausedRef.current = false;
    setMicrophoneTrackEnabled(false);
    const previousListeningState = "thinking";

    if (
      previousListeningState === "teacher_speaking" ||
      previousListeningState === "processing" ||
      previousListeningState === "muted"
    ) {
      studentCanRespondRef.current = false;
      setWaitingForReadyPressValue(false);
      listeningStateRef.current = previousListeningState;
      setStudentCanRespond(false);
      setListeningState(previousListeningState);
      setSessionStatus(
        previousListeningState === "teacher_speaking"
          ? "Interviewer speaking..."
          : previousListeningState === "processing"
          ? "Processing answer"
          : "Mic opening — wait for green"
      );
    } else {
      setThinkingAfterTutorPrompt("resumePractice");
    }

    appendLog("System", "Practice resumed.");
  };

  const waitForAssistantAudioEnd = (nextStatus = "awaiting_answer") => {
    if (promptMuteTimerRef.current) {
      clearTimeout(promptMuteTimerRef.current);
      promptMuteTimerRef.current = null;
    }

    const isStartupPrompt = !firstTutorUtteranceDoneRef.current;
    startupGuardRef.current = true;

    if (isPracticePausedRef.current) {
      setMicrophoneTrackEnabled(false);
      return;
    }
    setMicrophoneTrackEnabled(false);
    debugLog("[ORAL DEBUG] Mic disabled; waiting for assistant audio end", {
      nextStatus,
      isStartupPrompt,
    });
    if (nextStatus === "completed") {
      setSessionStatus("Completed");
      return;
    }
  };

  const releaseAssistantAudioGateAfterBuffer = (reason) => {
    const currentTurnId = frontendTurnIdRef.current;

    if (lastAudioGateReleasedTurnIdRef.current === currentTurnId) {
      oralVoiceDebugWarn("[ORAL VOICE DEBUG WARNING] Duplicate assistant audio gate release skipped", {
        reason,
        frontendTurnId: currentTurnId,
      });
      return;
    }

    lastAudioGateReleasedTurnIdRef.current = currentTurnId;

    debugLog(
      "[ORAL DEBUG] Assistant audio ended",
      getOralDebugSnapshot({
        reason,
        delayMs: POST_ASSISTANT_AUDIO_MIC_BUFFER_MS,
        frontendTurnId: currentTurnId,
      })
    );

    if (promptMuteTimerRef.current) {
      clearTimeout(promptMuteTimerRef.current);
      promptMuteTimerRef.current = null;
    }

    promptMuteTimerRef.current = setTimeout(() => {
      startupGuardRef.current = false;
      if (!firstTutorUtteranceDoneRef.current) {
        firstTutorUtteranceDoneRef.current = true;
        startupUnlockTimerRef.current = null;
      }

      if (sessionMetaRef.current.closed || isPracticePausedRef.current) {
        setMicrophoneTrackEnabled(false);
        promptMuteTimerRef.current = null;
        return;
      }

      const promptAction = pendingTutorPromptActionRef.current || "next_question";
      markPromptFinished(`${reason}: post-audio buffer elapsed`, currentTurnId);

      if (promptAction === "followup") {
        completeTutorPromptResponse(`${reason}: post-audio buffer elapsed`, currentTurnId);
      } else {
        setMicrophoneTrackEnabled(false);
        completeTutorPromptResponse(`${reason}: post-audio buffer elapsed`, currentTurnId);
      }

      promptMuteTimerRef.current = null;
    }, POST_ASSISTANT_AUDIO_MIC_BUFFER_MS);

    startupUnlockTimerRef.current = promptMuteTimerRef.current;
  };

  const fetchHtmlReport = async (
    sessionId,
    email,
    retries = 60,
    delayMs = 2000
  ) => {
    if (!sessionId || !email) return "";

    setReportStatus("Fetching DSA interview report...");

    for (let i = 0; i < retries; i += 1) {
      let nextPollStatus = "";
      try {
        const res = await fetch(`/api/oral/report/${sessionId}`, {
          credentials: "include",
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "X-User-Email": email,
          },
        });

        const data = await res.json();
        const payload = data?.data || data || {};

        if ((res.status === 401 || res.status === 403) && isDsaReportPublicSessionError(res.status, payload)) {
          const message = "Your session has expired. Please verify your email again.";
          stopSession();
          clearPublicAuthentication(message);
          setReportStatus(message);
          appendLog("System", message);
          return "";
        }

        const reportStatusValue = String(
          payload?.report_status || payload?.report_generation_status || ""
        ).toLowerCase();
        const reportError =
          payload?.report_generation_error ||
          payload?.error ||
          payload?.message ||
          "";
        const nextReportUrl =
          payload?.html_report_sas_url ||
          payload?.html_report_url ||
          payload?.report_url ||
          "";

        if (reportStatusValue === "failed") {
          const msg = reportError
            ? `DSA interview report generation failed: ${reportError}`
            : "DSA interview report generation failed.";
          setReportUrl("");
          setReportStatus(msg);
          appendLog("System", msg);
          return "";
        }

        if (nextReportUrl || reportStatusValue === "ready") {
          if (!nextReportUrl) {
            setReportStatus("DSA interview report is ready, but no report URL was returned.");
            appendLog("System", "DSA interview report is ready, but no report URL was returned.");
            return "";
          }
          setReportUrl(nextReportUrl);
          setReportStatus("DSA interview report ready.");
          appendLog("System", "DSA interview report is ready.");
          appendLog("System", nextReportUrl);
          try {
            const balances = await refreshDsaPublicBalances();
            if (balances) setPublicBalances(balances);
          } catch (_) {
            // The ready report remains usable if this informational refresh fails.
          }
          return nextReportUrl;
        }

        if (reportStatusValue === "pending") {
          nextPollStatus = `Preparing DSA interview report... (${i + 1}/${retries})`;
          setReportStatus(nextPollStatus);
        } else if (reportStatusValue === "not_started") {
          nextPollStatus = `DSA interview report generation is starting... (${i + 1}/${retries})`;
          setReportStatus(nextPollStatus);
        }
      } catch (err) {
        if (i === retries - 1) {
          const msg = err?.message || "Failed to fetch DSA interview report.";
          appendLog("System", `Report fetch error: ${msg}`);
        }
      }

      if (!nextPollStatus) {
        setReportStatus(`Preparing DSA interview report... (${i + 1}/${retries})`);
      }

      if (i < retries - 1) {
        await new Promise((resolve) => setTimeout(resolve, delayMs));
      }
    }

    setReportStatus(
      "Interview completed. A detailed DSA report template is not configured yet."
    );
    appendLog(
      "System",
      "Interview completed. A detailed DSA report template is not configured yet."
    );
    return "";
  };

  const languages =
    Array.isArray(topicsData?.languages) && topicsData.languages.length > 0
      ? topicsData.languages
      : DEFAULT_LANGUAGES;
  const standards =
    Array.isArray(topicsData?.levels) && topicsData.levels.length > 0
      ? topicsData.levels.map((level) => ({
          standard_id: level.level_id || level.standard_id || DSA_LEVEL_ID,
          standard_name:
            level.level_name || level.standard_name || "Primary DSA",
        }))
      : Array.isArray(topicsData?.standards) && topicsData.standards.length > 0
        ? topicsData.standards
        : [{ standard_id: DSA_LEVEL_ID, standard_name: "Primary DSA" }];
  const topicSets = normalizeQuestionSets(topicsData || {});

  const selectedTopicSet = useMemo(() => {
    return (
      topicSets.find((topicSet) => topicSet.topic_set_id === selectedTopicSetId) ||
      null
    );
  }, [topicSets, selectedTopicSetId]);

  const selectedTopic = useMemo(() => {
    if (!selectedTopicSet) {
      return null;
    }

    return {
      topic_id: selectedTopicSet.question_set_id,
      topic_title: selectedTopicSet.question_set_name,
      picture_stimulus: "",
      stimulus_media: selectedTopicSet.stimulus_media || null,
      questions: getQuestionSetQuestions(selectedTopicSet),
    };
  }, [selectedTopicSet]);

  const stimulus = useMemo(() => {
    return getTopicStimulusMedia(selectedTopic, sessionInfo);
  }, [selectedTopic, sessionInfo]);
  const hasStimulusMedia = Boolean(
    stimulus.displayUrl &&
      (stimulus.mediaType === "image" || stimulus.mediaType === "video")
  );

  const displayQuestions = useMemo(() => {
    const sessionQuestions = getOrderedQuestions(sessionInfo.questions || {});
    if (sessionQuestions.length > 0) {
      return sessionInfo.questions || {};
    }

    return selectedTopic?.questions || {};
  }, [selectedTopic, sessionInfo.questions]);

  const orderedDisplayQuestions = useMemo(() => {
    return getOrderedQuestions(displayQuestions);
  }, [displayQuestions]);

  const derivedUserEmail = useMemo(() => {
    const trimmedEmail = String(studentRegNumber || "").trim();
    if (trimmedEmail) {
      return trimmedEmail;
    }

    const safeName = String(studentName || "")
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_");

    if (!safeName) {
      return "";
    }

    return `dsa_${safeName}@student.local`;
  }, [studentName, studentRegNumber]);

  useEffect(() => {
    checkPublicSession();
    // Session restoration is a one-time page bootstrap.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function loadTopics() {
      if (!emailVerified || !resolvedClassContext) {
        setTopicsData(null);
        setLoadingTopics(false);
        setTopicsError("");
        return;
      }

      setLoadingTopics(true);
      setTopicsError("");

      try {
        const res = await axios.get("/api/oral/topics", {
          params: {
            practice_type: PRACTICE_TYPE,
            school_id: resolvedClassContext.school_id,
            teacher_id: resolvedClassContext.teacher_id,
            year: String(resolvedClassContext.year || ""),
            student_class: resolvedClassContext.student_class,
            language_id: "english",
            level_id: DSA_LEVEL_ID,
            // Temporary compatibility bridge for shared oral topics handlers.
            standard: DSA_LEVEL_ID,
          },
          ...PUBLIC_SESSION_CONFIG,
        });
        const payload = res.data?.data || res.data || {};

        if (!cancelled) {
          setTopicsData(payload);

          const payloadLanguages = getSafeLanguages(payload);
          const defaultLanguageId = getDefaultLanguageId(payload);
          const payloadQuestionSets = normalizeQuestionSets(payload);
          const defaultTopicSetId =
            payload?.question_set_id ||
            payload?.topic_set_id ||
            payloadQuestionSets[0]?.question_set_id ||
            DSA_FALLBACK_QUESTION_SETS[0].topic_set_id;
          const payloadStandards =
            Array.isArray(payload?.levels) && payload.levels.length > 0
              ? payload.levels.map((level) => ({
                  standard_id: level.level_id || level.standard_id || DSA_LEVEL_ID,
                }))
              : payload?.standards || [];
          const languageStillValid = payloadLanguages.some(
            (language) => getSafeLanguageId(language) === selectedLanguageId
          );
          const topicSetStillValid = payloadQuestionSets.some(
            (topicSet) => topicSet.question_set_id === selectedTopicSetId
          );
          const standardStillValid = payloadStandards.some(
            (standard) => standard.standard_id === selectedStandard
          );
          const nextStandard = standardStillValid
            ? selectedStandard
            : DSA_LEVEL_ID;
          const nextTopicSetId = topicSetStillValid
            ? selectedTopicSetId
            : defaultTopicSetId;

          setSelectedLanguageId(
            languageStillValid ? selectedLanguageId : defaultLanguageId
          );
          setSelectedTopicSetId(nextTopicSetId);
          setSelectedStandard(nextStandard);
          setSelectedTopicId(nextTopicSetId);

          console.log("Topics loaded, preserving selection:", {
            selectedLanguageId,
            selectedQuestionSetId: nextTopicSetId,
            selectedStandard: nextStandard,
          });
        }
      } catch (err) {
        if (isPublicSessionError(err)) {
          if (!cancelled) clearPublicAuthentication("Your session has expired. Please verify your email again.");
          return;
        }
        const msg =
          err?.response?.data?.error ||
          err?.message ||
          "Failed to load practice sets";
        if (!cancelled) {
          setTopicsError(msg);
        }
      } finally {
        if (!cancelled) {
          setLoadingTopics(false);
        }
      }
    }

    loadTopics();

    return () => {
      cancelled = true;
    };
  }, [
    emailVerified,
    resolvedClassContext,
    selectedLanguageId,
  ]);

  useEffect(() => {
    if (!selectedTopicSetId) {
      setSelectedTopicId("");
      return;
    }

    if (selectedTopicId !== selectedTopicSetId) {
      setSelectedTopicId(selectedTopicSetId);
    }
  }, [selectedTopicId, selectedTopicSetId]);

  useEffect(() => {
    return () => {
      if (completionCleanupFallbackTimerRef.current) {
        clearTimeout(completionCleanupFallbackTimerRef.current);
        completionCleanupFallbackTimerRef.current = null;
      }
      clearResponseActiveDiagnosticTimer();

      activeSessionKeyRef.current = "";
      voiceListenersCleanupRef.current();
      if (sessionRef.current) {
        try {
          sessionRef.current.close();
        } catch (_) {}
      }

      if (microphoneStreamRef.current) {
        microphoneStreamRef.current.getTracks().forEach((track) => track.stop());
        microphoneStreamRef.current = null;
      }
    };
  }, []);

  const clearCompletionCleanupFallbackTimer = (reason) => {
    if (!completionCleanupFallbackTimerRef.current) {
      return;
    }

    clearTimeout(completionCleanupFallbackTimerRef.current);
    completionCleanupFallbackTimerRef.current = null;
    debugLog(
      "[ORAL DEBUG] Completion fallback timer cleared",
      getOralDebugSnapshot({ reason })
    );
  };

  const startCompletionCleanupFallbackTimer = (sessionKey, reason) => {
    if (!sessionKey) {
      return;
    }

    if (completionCleanupFallbackTimerRef.current) {
      debugLog(
        "[ORAL DEBUG] Duplicate completion fallback ignored",
        getOralDebugSnapshot({ reason, sessionKey })
      );
      return;
    }

    completionCleanupFallbackTimerRef.current = setTimeout(() => {
      completionCleanupFallbackTimerRef.current = null;

      if (sessionKey !== activeSessionKeyRef.current) {
        debugLog(
          "[ORAL DEBUG] Stale completion fallback ignored",
          getOralDebugSnapshot({
            reason,
            eventSessionKey: sessionKey,
            activeSessionKey: activeSessionKeyRef.current || "",
          })
        );
        return;
      }

      if (completionCleanupStartedRef.current) {
        debugLog(
          "[ORAL DEBUG] Duplicate completion fallback ignored",
          getOralDebugSnapshot({ reason, sessionKey })
        );
        return;
      }

      debugLog(
        "[ORAL DEBUG] Completion fallback cleanup triggered",
        getOralDebugSnapshot({ reason, sessionKey })
      );
      runCompletionCleanupAfterFinalAudio(sessionKey, "completion fallback timeout");
    }, COMPLETION_CLEANUP_FALLBACK_MS);

    debugLog(
      "[ORAL DEBUG] Completion fallback timer started",
      getOralDebugSnapshot({
        reason,
        sessionKey,
        timeoutMs: COMPLETION_CLEANUP_FALLBACK_MS,
      })
    );
  };

  const stopSession = (options = {}) => {
    const {
      reason = "manual",
      statusAfter = "Disconnected",
      preserveCompletionState = false,
    } = options;
    if (reason !== "voice startup failed") voiceStartup.reset();
    debugLog(
      "[ORAL DEBUG] Full realtime cleanup started",
      getOralDebugSnapshot({ reason })
    );

    activeSessionKeyRef.current = "";
    voiceListenersCleanupRef.current();
    if (sessionRef.current) {
      intentionalCloseRef.current = true;
      try {
        sessionRef.current.close();
      } catch (_) {}
    }

    sessionRef.current = null;
    sessionMetaRef.current = {
      sessionId: "",
      currentQuestionIndex: 1,
      userEmail: "",
      userId: "",
      lastSpokenPrompt: "",
      closed: true,
    };

    if (microphoneStreamRef.current) {
      microphoneStreamRef.current.getTracks().forEach((track) => track.stop());
      microphoneStreamRef.current = null;
    }

    if (startupUnlockTimerRef.current) {
      clearTimeout(startupUnlockTimerRef.current);
      startupUnlockTimerRef.current = null;
    }

    if (promptMuteTimerRef.current) {
      clearTimeout(promptMuteTimerRef.current);
      promptMuteTimerRef.current = null;
    }

    if (completionCloseTimerRef.current) {
      clearTimeout(completionCloseTimerRef.current);
      completionCloseTimerRef.current = null;
    }
    clearCompletionCleanupFallbackTimer(`${reason}: stopSession`);

    activeSessionKeyRef.current = "";
    completionCleanupSessionKeyRef.current = "";
    completionCleanupStartedRef.current = false;
    startupGuardRef.current = false;
    firstTutorUtteranceDoneRef.current = false;
    micMutedRef.current = false;
    realtimeResponseActiveRef.current = false;
    realtimeResponseRequestPendingRef.current = false;
    realtimeResponseRequestSourceRef.current = "";
    activeRealtimeResponseIdRef.current = "";
    assistantAudioActiveRef.current = false;
    clearResponseActiveDiagnosticTimer();
    isPracticePausedRef.current = false;
    prePauseListeningStateRef.current = "ready";
    listeningStateRef.current = "muted";
    sessionStatusRef.current = "Disconnected";
    studentCanRespondRef.current = false;
    studentReadyToAnswerRef.current = false;
    setWaitingForReadyPressValue(false);
    pendingTutorPromptActionRef.current = "next_question";
    readyButtonPressedAtRef.current = "";
    ignoredTranscriptBeforeReadyRef.current = 0;
    frontendTurnIdRef.current = 0;
    lastAudioGateReleasedTurnIdRef.current = null;
    oralDebugTimelineRef.current = {
      promptStartedAt: "",
      promptFinishedAt: "",
      answerCaptureReadyAt: "",
      firstTranscriptDeltaAt: "",
      firstFinalTranscriptAt: "",
      transcriptBufferLength: 0,
      transcriptPreview: "",
      promptFinishedTurnId: null,
      answerCaptureReadyTurnId: null,
      readyButtonPressedAt: "",
      ignoredTranscriptBeforeReady: 0,
    };

    setIsConnected(false);
    setRealtimeCleanupInProgress(false);
    setStudentCanRespond(false);
    setReadyButtonPressedAt("");
    setIgnoredTranscriptBeforeReady(0);
    setListeningState("muted");
    setSessionStatus(statusAfter);
    debugLog(
      "[ORAL DEBUG] Full realtime cleanup completed",
      getOralDebugSnapshot({ reason, statusAfter, preserveCompletionState })
    );
  };

  const hasStaleRealtimeResources = () =>
    Boolean(
      sessionRef.current ||
        microphoneStreamRef.current ||
        startupUnlockTimerRef.current ||
        promptMuteTimerRef.current ||
        completionCloseTimerRef.current ||
        completionCleanupFallbackTimerRef.current ||
        activeSessionKeyRef.current ||
        completionCleanupSessionKeyRef.current ||
        completionCleanupStartedRef.current ||
        micMutedRef.current ||
        realtimeResponseActiveRef.current ||
        realtimeResponseRequestPendingRef.current ||
        activeRealtimeResponseIdRef.current ||
        assistantAudioActiveRef.current ||
        responseActiveDiagnosticTimerRef.current ||
        studentCanRespondRef.current ||
        studentReadyToAnswerRef.current ||
        waitingForReadyPressRef.current ||
        frontendTurnIdRef.current ||
        lastAudioGateReleasedTurnIdRef.current !== null
    );

  const runCompletionCleanupAfterFinalAudio = (sessionKey, reason) => {
    if (!sessionKey || sessionKey !== activeSessionKeyRef.current) {
      debugLog(
        "[ORAL DEBUG] Duplicate cleanup event ignored",
        getOralDebugSnapshot({
          reason,
          eventSessionKey: sessionKey || "",
          activeSessionKey: activeSessionKeyRef.current || "",
        })
      );
      return;
    }
    clearCompletionCleanupFallbackTimer(`${reason}: final audio cleanup`);

    if (
      completionCleanupStartedRef.current ||
      completionCleanupSessionKeyRef.current === sessionKey
    ) {
      debugLog(
        "[ORAL DEBUG] Duplicate cleanup event ignored",
        getOralDebugSnapshot({ reason, sessionKey })
      );
      return;
    }

    completionCleanupStartedRef.current = true;
    completionCleanupSessionKeyRef.current = sessionKey;
    setRealtimeCleanupInProgress(true);
    setStudentCanRespond(false);
    setWaitingForReadyPressValue(false);
    setListeningState("muted");
    setSessionStatus("Completed. Cleaning up voice session...");

    debugLog(
      "[ORAL DEBUG] Final completion audio stopped",
      getOralDebugSnapshot({ reason, sessionKey })
    );

    if (completionCloseTimerRef.current) {
      clearTimeout(completionCloseTimerRef.current);
    }

    completionCloseTimerRef.current = setTimeout(() => {
      stopSession({
        reason: `completion cleanup: ${reason}`,
        statusAfter: "Completed",
        preserveCompletionState: true,
      });
      debugLog(
        "[ORAL DEBUG] Report polling continuing after realtime cleanup",
        getOralDebugSnapshot({
          reason,
          reportStatus,
          reportUrl,
        })
      );
    }, 250);
  };

  const buildEvaluateTurnTool = () =>
    tool({
      name: "evaluate_oral_turn",
      description:
        "Call this after every student answer. Send the student's spoken answer transcript to the backend. The backend decides whether to follow up, ask the next question, or complete the session. You must then speak the returned spoken_prompt exactly.",
      parameters: z.object({
        transcript: z
          .string()
          .describe("The student's spoken answer transcript."),
      }),
      async execute({ transcript }) {
        const meta = sessionMetaRef.current;
        const transcriptText = String(transcript || "");
        debugLog(
          "[ORAL DEBUG] evaluate_oral_turn execute",
          getOralDebugSnapshot({
            reason: "evaluate_oral_turn execute",
            transcriptLength: transcriptText.length,
            transcriptPreview: transcriptText.slice(0, 200),
          })
        );
        markFirstFinalTranscript("evaluate_oral_turn tool execute", transcriptText);

        if (isPracticePausedRef.current) {
          appendLog("System", "Evaluation ignored because practice is paused.");
          debugLog(
            "[ORAL DEBUG] Evaluation ignored while paused",
            getOralDebugSnapshot({
              transcriptLength: transcriptText.length,
              transcriptPreview: transcriptText.slice(0, 200),
            })
          );
          return {
            action: "paused",
            spoken_prompt: "",
            current_question_index: meta.currentQuestionIndex || 1,
            status: "paused",
            instruction:
              "Do not speak. Do not call any tools until the student resumes.",
          };
        }

        if (startupGuardRef.current) {
          appendLog("System", "Ignoring input during startup guard.");
          return {
            action: "followup",
            spoken_prompt: "",
            current_question_index:
              sessionMetaRef.current.currentQuestionIndex || 1,
            status: "awaiting_answer",
            instruction:
              "Do not speak. Ignore this input and wait for the next real student answer.",
          };
        }

        if (!studentReadyToAnswerRef.current) {
          const ignoredCount = ignoredTranscriptBeforeReadyRef.current + 1;
          ignoredTranscriptBeforeReadyRef.current = ignoredCount;
          setIgnoredTranscriptBeforeReady(ignoredCount);
          oralDebugTimelineRef.current = {
            ...oralDebugTimelineRef.current,
            ignoredTranscriptBeforeReady: ignoredCount,
          };
          appendLog("System", "Transcript ignored. Student has not pressed Ready to Answer.");
          debugLog(
            "[ORAL DEBUG] Evaluation ignored before Ready to Answer",
            getOralDebugSnapshot({
              transcriptLength: transcriptText.length,
              transcriptPreview: transcriptText.slice(0, 200),
              ignoredTranscriptBeforeReady: ignoredCount,
            })
          );
          return {
            action: "awaiting_ready",
            spoken_prompt: "",
            current_question_index:
              sessionMetaRef.current.currentQuestionIndex || 1,
            status: "thinking",
            instruction:
              "Do not speak. Ignore this transcript and wait until the student presses Ready to Answer.",
          };
        }

        if (!meta.sessionId) {
          return {
            action: "error",
            spoken_prompt:
              "The session is not ready. Please start a new oral practice.",
            current_question_index: 1,
            status: "error",
            instruction:
              "Speak the spoken_prompt exactly and do not add anything else.",
          };
        }

        studentReadyToAnswerRef.current = false;
        studentCanRespondRef.current = false;
        setWaitingForReadyPressValue(false);
        setStudentCanRespond(false);
        appendLog("Student", transcript);

        if (sessionMetaRef.current.closed) {
          return {
            action: "completed",
            spoken_prompt: "This oral practice session is already completed.",
            current_question_index:
              sessionMetaRef.current.currentQuestionIndex || 1,
            status: "completed",
            instruction:
              "Speak the spoken_prompt exactly and do not add anything else.",
          };
        }

        try {
          listeningStateRef.current = "processing";
          setListeningState("processing");
          appendLog(
            "System",
            `Sending transcript to backend for question ${meta.currentQuestionIndex}`
          );
          debugLog(
            "[ORAL DEBUG] /api/oral/process-turn POST",
            getOralDebugSnapshot({
              questionIndex: meta.currentQuestionIndex,
              transcriptLength: transcriptText.length,
              transcriptPreview: transcriptText.slice(0, 200),
              transcriptFull: transcript || "",
              reason: "process-turn POST",
            })
          );

          const debugFrontend = getOralDebugSnapshot({
            frontendTurnId: frontendTurnIdRef.current,
            currentQuestionIndex: meta.currentQuestionIndex,
            questionIndex: meta.currentQuestionIndex,
            status: listeningStateRef.current || sessionStatusRef.current || "",
            micMuted: micMutedRef.current,
            answerCaptureReady: Boolean(studentCanRespondRef.current),
            transcriptBufferLength: transcriptText.length,
            transcriptPreview: transcriptText.slice(0, 200),
            transcript: transcriptText,
            sentAt: new Date().toISOString(),
            reason: "process-turn payload",
          });
          oralVoiceDebugLog("[ORAL VOICE DEBUG] Sending transcript to backend", {
            ...debugFrontend,
            transcript: transcriptText,
          });

          const res = await axios.post("/api/oral/process-turn", {
            practice_type: PRACTICE_TYPE,
            session_id: meta.sessionId,
            question_index: meta.currentQuestionIndex,
            transcript,
            user_email: meta.userEmail,
            debug_frontend: debugFrontend,
          }, PUBLIC_SESSION_CONFIG);

          const result = res.data?.data || res.data || {};

          const nextQuestionIndex =
            result.current_question_index || meta.currentQuestionIndex || 1;
          const spokenPrompt = result.spoken_prompt || "";
          // Deprecated timing value retained only for backend/debug payloads.
          // Do not use prompt_mute_ms for mic gating; use audio output events.
          const promptMuteMs = Number(result.prompt_mute_ms) || 0;
          const nextStatus = result.status || "awaiting_answer";
          const nextAction = result.action || "followup";
          pendingTutorPromptActionRef.current =
            nextStatus === "completed" ? "completed" : nextAction;

          appendLog(
            "System",
            `Backend action: ${result.action || "unknown"}, status: ${
              result.status || "unknown"
            }`
          );
          debugLog("[ORAL DEBUG] Backend response received", {
            backendAction: result.action || "unknown",
            backendStatus: result.status || "unknown",
            spokenPrompt: spokenPrompt,
            nextQuestionIndex,
          });
          oralVoiceDebugLog("[ORAL VOICE DEBUG] Backend response received", {
            reason: "process-turn response",
            backendAction: result.action || "unknown",
            backendStatus: result.status || "unknown",
            nextQuestionIndex,
            transcript: transcriptText,
          });

          sessionMetaRef.current = {
            ...sessionMetaRef.current,
            currentQuestionIndex: nextQuestionIndex,
            lastSpokenPrompt: spokenPrompt,
            closed: nextStatus === "completed",
          };

          setSessionInfo((prev) => ({
            ...prev,
            currentQuestionIndex: nextQuestionIndex,
            status: nextStatus,
            spokenPrompt: spokenPrompt,
          }));

          setSessionStatus(
            result.status === "completed"
              ? "Completed. Finishing final message..."
              : `Connected — ${result.action || "awaiting_answer"}`
          );

          if (nextStatus === "completed") {
            setRealtimeCleanupInProgress(true);
            setStudentCanRespond(false);
            setWaitingForReadyPressValue(false);
            setListeningState("muted");
            sessionMetaRef.current = {
              ...sessionMetaRef.current,
              currentQuestionIndex: nextQuestionIndex,
              lastSpokenPrompt: spokenPrompt,
              closed: true,
            };
            debugLog(
              "[ORAL DEBUG] Topic marked completed",
              getOralDebugSnapshot({
                backendAction: result.action || "unknown",
                backendStatus: result.status || "unknown",
                sessionKey: activeSessionKeyRef.current,
              })
            );
            debugLog(
              "[ORAL DEBUG] Waiting for final completion audio",
              getOralDebugSnapshot({
                sessionKey: activeSessionKeyRef.current,
                hasSpokenPrompt: Boolean(spokenPrompt),
              })
            );
            startCompletionCleanupFallbackTimer(
              activeSessionKeyRef.current,
              "backend completed"
            );
            fetchHtmlReport(meta.sessionId, meta.userEmail);
          }

          appendLog("Interviewer", spokenPrompt);

          if (spokenPrompt && !isPracticePausedRef.current) {
            markPromptStarted("backend spoken prompt", spokenPrompt);
            setListeningState("teacher_speaking");
            waitForAssistantAudioEnd(nextStatus);
          } else if (nextStatus === "completed") {
            runCompletionCleanupAfterFinalAudio(
              activeSessionKeyRef.current,
              "completed without spoken prompt"
            );
          } else if (nextStatus !== "completed" && !isPracticePausedRef.current) {
            completeTutorPromptResponse("backend response without spoken prompt");
          }

          return {
            action: nextAction,
            spoken_prompt: spokenPrompt,
            prompt_mute_ms: promptMuteMs,
            current_question_index: nextQuestionIndex,
            status: nextStatus,
            instruction:
              "Speak the spoken_prompt exactly. Do not change wording. Do not invent a new question.",
          };
        } catch (err) {
          if (isPublicSessionError(err)) {
            stopSession();
            clearPublicAuthentication("Your session has expired. Please verify your email again.");
            return { action: "error", spoken_prompt: "Your session has expired. Please verify your email again.", status: "error" };
          }
          const msg =
            err?.response?.data?.error ||
            err?.message ||
            "Failed to process oral turn.";

          appendLog("System", `Backend error: ${msg}`);
          if (!isPracticePausedRef.current) {
            setThinkingAfterTutorPrompt("backend error");
          }

          return {
            action: "error",
            spoken_prompt:
              "There was a temporary problem processing the answer. Please try again.",
            current_question_index: meta.currentQuestionIndex,
            status: "error",
            instruction:
              "Speak the spoken_prompt exactly and do not add anything else.",
          };
        }
      },
    });

  const refreshSelectedTopicDisplay = () => {
    setSessionInfo((prev) => ({
      ...prev,
      questions: getQuestionSetQuestions(selectedTopicSet),
    }));
  };

  const handleSendEmailCode = async () => {
    const normalizedEmail = studentRegNumber.trim().toLowerCase();

    if (!normalizedEmail) {
      setEmailVerificationStatus("Please enter your email address.");
      return;
    }

    setSendingEmailCode(true);
    setEmailVerified(false);
    setTopicsData(null);
    setTopicsError("");
    setSelectedStandard(DSA_LEVEL_ID);
    setSelectedTopicSetId("");
    setSelectedTopicId("");
    setEmailVerificationStatus("Sending email verification code...");

    try {
      await axios.post("/api/public/oral/send-email-code", {
        email: normalizedEmail,
      });
      setEmailCodeSent(true);
      setEmailVerificationStatus(`Verification code sent to ${normalizedEmail}.`);
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        "Failed to send email verification code.";
      setEmailVerificationStatus(msg);
    } finally {
      setSendingEmailCode(false);
    }
  };

  const handleVerifyEmailCode = async () => {
    const normalizedEmail = studentRegNumber.trim().toLowerCase();

    if (!normalizedEmail) {
      setEmailVerificationStatus("Please enter your email address.");
      return;
    }

    if (!publicEmailCode.trim()) {
      setEmailVerificationStatus("Please enter the email verification code.");
      return;
    }

    setVerifyingEmailCode(true);
    setEmailVerificationStatus("Verifying email code...");

    try {
      const res = await axios.post("/api/public/oral/verify-email-code", {
        email: normalizedEmail,
        code: publicEmailCode.trim(),
        marketing_email_consent: false,
      }, PUBLIC_SESSION_CONFIG);

      const verifiedUserId =
        res.data?.user_id || res.data?.data?.user_id || "";
      const verifiedEmail =
        res.data?.user_email ||
        res.data?.data?.user_email ||
        normalizedEmail;

      if (!verifiedUserId) {
        throw new Error("Email verified, but no user ID was returned.");
      }

      setUserId(verifiedUserId);
      setStudentRegNumber(verifiedEmail);
      setEmailVerified(true);
      setEmailVerificationStatus(
        "Email verified. You may start the DSA interview practice."
      );
      const account = getPublicSessionPayload(await getPublicSession());
      if (account.authenticated) { setPublicUser(account.user); setPublicBalances(account.balances); }
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        "Failed to verify email code.";
      setEmailVerificationStatus(msg);
      setEmailVerified(false);
    } finally {
      setVerifyingEmailCode(false);
    }
  };

  const handlePublicLogout = async () => {
    if (startRequestPendingRef.current || voiceStartup.busy) return;
    setLoggingOut(true);
    try {
      await logoutPublicSession();
      stopSession(); clearPublicAuthentication(); setSessionStatus("Not started");
    } catch (_) { setEmailVerificationStatus("Logout failed. Please try again."); }
    finally { setLoggingOut(false); }
  };

  const startVoiceSession = async () => {
    if (startRequestPendingRef.current || voiceStartup.busy || voiceStartup.error || isConnected) return;
    if (!emailVerified) {
      setSessionStatus("Please verify your email before starting.");
      return;
    }

    if (!selectedStandard) {
      setSessionStatus("Please select a level.");
      return;
    }

    if (!selectedTopicSetId) {
      setSessionStatus("Please choose a DSA interview practice set.");
      return;
    }

    if (!studentName.trim()) {
      setSessionStatus("Please enter your name.");
      return;
    }

    if (!derivedUserEmail) {
      setSessionStatus("Please enter your email.");
      return;
    }

    if (hasStaleRealtimeResources()) {
      debugLog(
        "[ORAL DEBUG] Stale session found before new session start",
        getOralDebugSnapshot({
          hasSession: Boolean(sessionRef.current),
          hasMicrophoneStream: Boolean(microphoneStreamRef.current),
          hasCompletionTimer: Boolean(completionCloseTimerRef.current),
          activeSessionKey: activeSessionKeyRef.current || "",
        })
      );
      stopSession({
        reason: "defensive cleanup before new session start",
        statusAfter: "Starting session...",
      });
      debugLog(
        "[ORAL DEBUG] Defensive cleanup completed",
        getOralDebugSnapshot({ reason: "before new session start" })
      );
    }

    startRequestPendingRef.current = true;
    setStartingSession(true);
    setRealtimeCleanupInProgress(false);
    intentionalCloseRef.current = false;
    studentCanRespondRef.current = false;
    studentReadyToAnswerRef.current = false;
    pendingTutorPromptActionRef.current = "next_question";
    readyButtonPressedAtRef.current = "";
    ignoredTranscriptBeforeReadyRef.current = 0;
    listeningStateRef.current = "muted";
    sessionStatusRef.current = "Starting session...";
    setStudentCanRespond(false);
    setListeningState("muted");
    isPracticePausedRef.current = false;
    prePauseListeningStateRef.current = "ready";
    setSessionStatus("Starting session...");
    setEventLog([]);
    setReportUrl("");
    setReportStatus("");
    frontendTurnIdRef.current = 0;
    lastAudioGateReleasedTurnIdRef.current = null;
    oralDebugTimelineRef.current = {
      promptStartedAt: "",
      promptFinishedAt: "",
      answerCaptureReadyAt: "",
      firstTranscriptDeltaAt: "",
      firstFinalTranscriptAt: "",
      transcriptBufferLength: 0,
      transcriptPreview: "",
      promptFinishedTurnId: null,
      answerCaptureReadyTurnId: null,
      readyButtonPressedAt: "",
      ignoredTranscriptBeforeReady: 0,
    };
    setReadyButtonPressedAt("");
    setIgnoredTranscriptBeforeReady(0);

    try {
      // 1) Create backend session
      const fallbackUserEmail = derivedUserEmail;
      const selectedQuestionSetId = selectedTopicSetId;
      const startPayload = {
        standard: selectedStandard || DSA_LEVEL_ID,
        level_id: selectedStandard || DSA_LEVEL_ID,
        user_email: fallbackUserEmail,
        language_id: "english",
        question_set_id: selectedQuestionSetId,
        activity_set_id: selectedQuestionSetId,
        school_id: resolvedClassContext.school_id,
        teacher_id: resolvedClassContext.teacher_id,
        year: String(resolvedClassContext.year || ""),
        student_class: resolvedClassContext.student_class,
        student_name: studentName.trim(),
        student_reg_number: userId || studentRegNumber.trim(),
        student_verification_code: "",
        practice_type: PRACTICE_TYPE,
        pause_duration_mode: selectedPauseDurationMode,
      };
      console.log("Starting oral session with payload:", startPayload);
      const startRes = await axios.post("/api/oral/start-session", startPayload, PUBLIC_SESSION_CONFIG);

      const sessionPayload = startRes.data?.data || {};
      await voiceStartup.start(async (attempt) => {
        intentionalCloseRef.current = false;
        setRealtimeCleanupInProgress(false);
        const sessionUserContext = sessionPayload.user_context || {};
        const sessionUserEmail =
          sessionUserContext.user_email ||
          sessionPayload.user_email ||
          fallbackUserEmail;
        const openingPrompt = sessionPayload.spoken_prompt || "";

        setSessionInfo({
          sessionId: sessionPayload.session_id || "",
          status: sessionPayload.status || "",
          languageId: sessionPayload.language_id || selectedLanguageId || "english",
          standard: sessionPayload.standard || "",
          topicSetId:
            sessionPayload.question_set_id ||
            sessionPayload.topic_set_id ||
            selectedTopicSetId ||
            "",
          topicId: sessionPayload.topic_id || "",
          topicTitle: sessionPayload.topic_title || "",
          pictureStimulus: sessionPayload.picture_stimulus || "",
          pictureStimulusUrl: sessionPayload.picture_stimulus_url || "",
          stimulusMedia: sessionPayload.stimulus_media || {
            media_type: sessionPayload.picture_stimulus_url ? "image" : "none",
            source_type: "legacy_picture_url",
            display_url: sessionPayload.picture_stimulus_url || "",
            video_url: "",
            blob_name: "",
            description: sessionPayload.picture_stimulus || "",
          },
          questions: sessionPayload.questions || {},
          questionCount:
            sessionPayload.question_count ||
            getOrderedQuestions(sessionPayload.questions || {}).length,
          currentQuestionIndex: sessionPayload.current_question_index || 1,
          spokenPrompt: openingPrompt,
        });

        // 2) Load realtime agent configuration from backend
        setSessionStatus("Fetching Realtime config...");
        const realtimeConfigRes = await attempt.wait("loading_configuration", () => axios.get("/api/oral/realtime-config", {
          params: {
            language_id: "english",
            practice_type: PRACTICE_TYPE,
            pause_duration_mode: selectedPauseDurationMode,
          },
          ...PUBLIC_SESSION_CONFIG,
        }), CONNECTION_TIMEOUT_MS);
        const realtimeConfig = realtimeConfigRes.data?.data || {};
        const backendAudioConfig = realtimeConfig.audio || {};
        const backendInputConfig = backendAudioConfig.input || {};
        const backendTurnDetection =
          backendInputConfig.turn_detection ||
          backendInputConfig.turnDetection ||
          {};
        const sdkTurnDetection =
          mapBackendTurnDetectionToSdk(backendTurnDetection);
        debugLog("[ORAL DEBUG] Realtime turn detection config from backend", {
          selectedPauseDurationMode,
          backendTurnDetection,
          sdkTurnDetection,
        });

        if (
          !realtimeConfig.agent_name ||
          !realtimeConfig.instructions ||
          !realtimeConfig.voice ||
          !realtimeConfig.model
        ) {
          throw new Error("Realtime config missing required fields");
        }

        // 3) Get ephemeral realtime token
        setSessionStatus("Fetching Realtime token...");
        debugLog(
          "[ORAL DEBUG] Fresh realtime token requested",
          getOralDebugSnapshot({ practiceType: PRACTICE_TYPE })
        );
        const tokenRes = await attempt.wait("requesting_token", () => axios.get("/api/realtime-token", {
          params: {
            practice_type: PRACTICE_TYPE,
            pause_duration_mode: selectedPauseDurationMode,
          },
          ...PUBLIC_SESSION_CONFIG,
        }), CONNECTION_TIMEOUT_MS);
        const clientSecret =
          tokenRes.data?.data?.client_secret || tokenRes.data?.client_secret || "";

        if (!clientSecret) {
          throw new Error("Realtime token missing client_secret");
        }

        // 4) Create agent with local tool
        const evaluateOralTurnTool = buildEvaluateTurnTool();

        const agent = new RealtimeAgent({
          name: realtimeConfig.agent_name,
          instructions: realtimeConfig.instructions,
          voice: realtimeConfig.voice,
          tools: [evaluateOralTurnTool],
        });

        const microphoneStream = await attempt.wait("opening_microphone", () => navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          },
        }), MICROPHONE_TIMEOUT_MS, (stream) => stream.getTracks().forEach((track) => track.stop()));

        microphoneStreamRef.current = microphoneStream;
        setMicrophoneTrackEnabled(false);
        debugLog("[ORAL DEBUG] Mic disabled", {
          reason: "manual start; disabled until student can respond",
        });

        const realtimeSession = new RealtimeSession(agent, {
          model: realtimeConfig.model,
          transport: new OpenAIRealtimeWebRTC({
            mediaStream: microphoneStream,
          }),
          config: {
            audio: {
              input: {
                turnDetection: sdkTurnDetection,
              },
            },
          },
        });
        const listeners = [];
        const listenToRealtime = (type, handler) => {
          realtimeSession.on(type, handler);
          listeners.push([type, handler]);
        };
        voiceListenersCleanupRef.current = () => {
          listeners.forEach(([type, handler]) => realtimeSession.off?.(type, handler));
          listeners.length = 0;
        };
        const sessionKey = `${sessionPayload.session_id || "dsa"}:${Date.now()}`;
        activeSessionKeyRef.current = sessionKey;
        completionCleanupSessionKeyRef.current = "";
        completionCleanupStartedRef.current = false;
        realtimeResponseRequestPendingRef.current = false;
        realtimeResponseActiveRef.current = false;
        realtimeResponseRequestSourceRef.current = "";
        activeRealtimeResponseIdRef.current = "";
        assistantAudioActiveRef.current = false;
        clearResponseActiveDiagnosticTimer();
        debugLog(
          "[ORAL DEBUG] New realtime session created",
          getOralDebugSnapshot({
            sessionKey,
            backendSessionId: sessionPayload.session_id || "",
          })
        );

        listenToRealtime("event", (event) => {
          if (sessionKey !== activeSessionKeyRef.current) {
            debugLog("[ORAL DEBUG] Duplicate cleanup event ignored", {
              reason: "stale realtime event",
              eventSessionKey: sessionKey,
              activeSessionKey: activeSessionKeyRef.current || "",
              type: event?.type || "",
            });
            return;
          }
          const eventType = event?.type || "";
          const trackedTypes = new Set([
            "response.created",
            "input_audio_buffer.speech_started",
            "input_audio_buffer.speech_stopped",
            "input_audio_buffer.committed",
            "conversation.item.input_audio_transcription.delta",
            "conversation.item.input_audio_transcription.completed",
            "response.audio_transcript.delta",
            "response.audio_transcript.done",
            "response.done",
            "response.cancelled",
            "response.failed",
            "error",
          ]);

          if (!trackedTypes.has(eventType)) {
            return;
          }

          const transcriptText = String(
            event?.transcript || event?.text || event?.delta || ""
          );
          const responseId = event?.response_id || event?.response?.id || "";

          debugLog("[ORAL DEBUG] Realtime event", {
            ...getOralDebugSnapshot({
              transcriptLength: transcriptText.length,
              transcriptPreview: transcriptText.slice(0, 200),
            }),
            type: eventType,
            transcript: event?.transcript || event?.text || "",
            itemId: event?.item_id || event?.item?.id || "",
            responseId,
            audioStartMs: event?.audio_start_ms,
            audioEndMs: event?.audio_end_ms,
            payload: {
              error: event?.error || null,
              delta: event?.delta || "",
              status: event?.status || "",
            },
          });

          if (eventType === "response.created") {
            realtimeResponseRequestPendingRef.current = false;
            realtimeResponseActiveRef.current = true;
            activeRealtimeResponseIdRef.current = responseId;
            oralVoiceDebugLog("[ORAL VOICE DEBUG] Realtime AI response start", {
              reason: "response.created event",
              responseId,
            });
          }

          if (eventType === "conversation.item.input_audio_transcription.delta") {
            markFirstTranscriptDelta("realtime transcription delta event", transcriptText);
          }

          if (eventType === "conversation.item.input_audio_transcription.completed") {
            markFirstFinalTranscript("realtime transcription completed event", transcriptText);
          }

          if (eventType === "input_audio_buffer.speech_started" && !studentCanRespondRef.current) {
            oralVoiceDebugWarn("[ORAL VOICE DEBUG WARNING] Speech started before answerCaptureReady", {
              reason: "input_audio_buffer.speech_started",
            });
          }

          if (
            eventType === "response.done" ||
            eventType === "response.cancelled" ||
            eventType === "response.failed"
          ) {
            if (
              activeRealtimeResponseIdRef.current &&
              responseId &&
              responseId !== activeRealtimeResponseIdRef.current
            ) {
              debugLog("[ORAL DEBUG] Ignored stale realtime response terminal event", {
                type: eventType,
                responseId,
                activeResponseId: activeRealtimeResponseIdRef.current,
              });
              return;
            }
            realtimeResponseRequestPendingRef.current = false;
            activeRealtimeResponseIdRef.current = "";
            maybeClearRealtimeResponseActive(eventType);
            debugLog("[ORAL DEBUG] Realtime response terminal event", {
              ...getOralDebugSnapshot({
                transcriptLength: transcriptText.length,
                transcriptPreview: transcriptText.slice(0, 200),
              }),
              type: eventType,
              responseId,
              assistantAudioActive: assistantAudioActiveRef.current,
            });
            oralVoiceDebugLog("[ORAL VOICE DEBUG] Realtime AI response done / prompt finished", {
              reason: eventType,
              responseId,
            });
          }

          if (eventType === "error") {
            const { code: eventErrorCode, message: eventErrorMessage } =
              normalizeRealtimeError(event);
            debugLog("[ORAL DEBUG] Realtime event", {
              ...getOralDebugSnapshot({
                transcriptLength: transcriptText.length,
                transcriptPreview: transcriptText.slice(0, 200),
              }),
              type: eventType,
              errorCode: eventErrorCode,
              errorMessage: eventErrorMessage,
              rawError: event?.error || null,
            });
            if (eventErrorCode === "conversation_already_has_active_response") {
              const pendingBeforeClear = realtimeResponseRequestPendingRef.current;
              realtimeResponseRequestPendingRef.current = false;
              maybeClearRealtimeResponseActive("duplicate response request rejected");
              oralVoiceDebugWarn("[ORAL VOICE DEBUG WARNING] Recoverable realtime duplicate response rejected", {
                errorCode: eventErrorCode,
                errorMessage: eventErrorMessage,
                responseRequestPendingBeforeClear: pendingBeforeClear,
                responseRequestPendingAfterClear: realtimeResponseRequestPendingRef.current,
                activeResponseId: activeRealtimeResponseIdRef.current,
                assistantAudioActive: assistantAudioActiveRef.current,
                sessionKey,
                requestSource: realtimeResponseRequestSourceRef.current,
              });
              return;
            }
            realtimeResponseRequestPendingRef.current = false;
            activeRealtimeResponseIdRef.current = "";
            assistantAudioActiveRef.current = false;
            maybeClearRealtimeResponseActive("realtime error event");
          }
        });

        listenToRealtime("error", (err) => {
          if (sessionKey !== activeSessionKeyRef.current) {
            debugLog("[ORAL DEBUG] Duplicate cleanup event ignored", {
              reason: "stale realtime error",
              eventSessionKey: sessionKey,
              activeSessionKey: activeSessionKeyRef.current || "",
            });
            return;
          }
          if (intentionalCloseRef.current || sessionMetaRef.current.closed) {
            console.warn("Ignoring Realtime error during intentional shutdown:", err);
            return;
          }
          const { code: errorCode, message: errorMessage } =
            normalizeRealtimeError(err);

          debugLog("[ORAL DEBUG] Realtime event", {
            ...getOralDebugSnapshot(),
            type: "error",
            payload: {
              message: errorMessage || String(err || ""),
              code: errorCode,
            },
          });
          if (errorCode === "conversation_already_has_active_response") {
            const pendingBeforeClear = realtimeResponseRequestPendingRef.current;
            realtimeResponseRequestPendingRef.current = false;
            maybeClearRealtimeResponseActive("duplicate response request rejected");
            oralVoiceDebugWarn("[ORAL VOICE DEBUG WARNING] Recoverable realtime duplicate response rejected", {
              errorCode,
              errorMessage,
              responseRequestPendingBeforeClear: pendingBeforeClear,
              responseRequestPendingAfterClear: realtimeResponseRequestPendingRef.current,
              activeResponseId: activeRealtimeResponseIdRef.current,
              assistantAudioActive: assistantAudioActiveRef.current,
              sessionKey,
              requestSource: realtimeResponseRequestSourceRef.current,
            });
            return;
          }
          realtimeResponseRequestPendingRef.current = false;
          activeRealtimeResponseIdRef.current = "";
          assistantAudioActiveRef.current = false;
          maybeClearRealtimeResponseActive("realtime error");
          console.error("Realtime error:", err);
          appendLog("System", "Realtime error occurred.");
          if (waitingForReadyPressRef.current) {
            debugLog("[ORAL DEBUG] Realtime error ignored while waiting for Ready press", {
              errorCode,
              errorMessage: errorMessage || String(err || ""),
            });
            return;
          }
          setListeningState("muted");
          setSessionStatus("Realtime error");
        });

        listenToRealtime("audio_start", () => {
          if (sessionKey !== activeSessionKeyRef.current) {
            debugLog("[ORAL DEBUG] Duplicate cleanup event ignored", {
              reason: "stale audio_start",
              eventSessionKey: sessionKey,
              activeSessionKey: activeSessionKeyRef.current || "",
            });
            return;
          }
          oralVoiceDebugLog("[ORAL VOICE DEBUG] Realtime AI response start", {
            reason: "audio_start",
          });
          assistantAudioActiveRef.current = true;
          realtimeResponseActiveRef.current = true;
          if (promptMuteTimerRef.current) {
            clearTimeout(promptMuteTimerRef.current);
            promptMuteTimerRef.current = null;
          }
          disableMicrophoneTrack("assistant audio output started");
          setWaitingForReadyPressValue(false);
          if (isPracticePausedRef.current) {
            prePauseListeningStateRef.current = "teacher_speaking";
            return;
          }
          studentReadyToAnswerRef.current = false;
          studentCanRespondRef.current = false;
          listeningStateRef.current = "teacher_speaking";
          sessionStatusRef.current = "Interviewer speaking...";
          setStudentCanRespond(false);
          setListeningState("teacher_speaking");
          setSessionStatus("Interviewer speaking...");
        });

        listenToRealtime("audio_stopped", () => {
          if (sessionKey !== activeSessionKeyRef.current) {
            debugLog("[ORAL DEBUG] Duplicate cleanup event ignored", {
              reason: "stale audio_stopped",
              eventSessionKey: sessionKey,
              activeSessionKey: activeSessionKeyRef.current || "",
            });
            return;
          }
          if (sessionMetaRef.current.closed) {
            assistantAudioActiveRef.current = false;
            setMicrophoneTrackEnabled(false);
            setStudentCanRespond(false);
            setListeningState("muted");
            setSessionStatus("Completed. Cleaning up voice session...");
            setWaitingForReadyPressValue(false);
            runCompletionCleanupAfterFinalAudio(sessionKey, "audio_stopped");
            return;
          }

          if (isPracticePausedRef.current) {
            prePauseListeningStateRef.current = "thinking";
            studentReadyToAnswerRef.current = false;
            setMicrophoneTrackEnabled(false);
            studentCanRespondRef.current = false;
            setWaitingForReadyPressValue(false);
            return;
          }

          if (waitingForReadyPressRef.current) {
            debugLog("[ORAL DEBUG] Duplicate audio_stopped ignored while waiting for Ready press");
            return;
          }

          const promptAction = pendingTutorPromptActionRef.current || "next_question";
          if (promptAction === "followup") {
            setListeningState("muted");
          }
          assistantAudioActiveRef.current = false;
          maybeClearRealtimeResponseActive("audio_stopped");
          releaseAssistantAudioGateAfterBuffer("audio_stopped");
        });

        sessionRef.current = realtimeSession;
        sessionMetaRef.current = {
          sessionId: sessionPayload.session_id || "",
          currentQuestionIndex: sessionPayload.current_question_index || 1,
          userEmail: sessionUserEmail,
          userId: sessionUserContext.user_id || sessionPayload.user_id || "",
          lastSpokenPrompt: openingPrompt,
          closed: false,
        };
        startupGuardRef.current = true;
        setListeningState("teacher_speaking");
        firstTutorUtteranceDoneRef.current = false;

        // 5) Connect
        setSessionStatus("Connecting voice agent...");
        await attempt.wait("connecting_realtime", () => realtimeSession.connect({ apiKey: clientSecret }), CONNECTION_TIMEOUT_MS, () => { try { realtimeSession.close(); } catch (_) {} });
        setIsConnected(true);

        appendLog("Interviewer", openingPrompt);
        pendingTutorPromptActionRef.current = "next_question";
        setSessionStatus("Connected — DSA interview started");

        // 6) Force the opening question
        sendTutorRealtimeMessage(
          realtimeSession,
          `Begin the DSA interview now. Speak exactly this prompt and nothing else: ${openingPrompt}`,
          "startup_force_prompt",
          sessionKey
        );
        setSessionStatus("Interviewer speaking first question...");
        waitForAssistantAudioEnd("awaiting_answer");
      }, (failure) => {
        stopSession({ reason: "voice startup failed", statusAfter: "Voice could not start. Try again below." });
        if (isPublicSessionError(failure)) {
          clearPublicAuthentication("Your session has expired. Please verify your email again.");
          return false;
        }
      });
    } catch (err) {
      if (isPublicSessionError(err)) {
        clearPublicAuthentication("Your session has expired. Please verify your email again.");
        setSessionStatus("Your session has expired. Please verify your email again.");
        stopSession();
        return;
      }
      console.error(err);
      const msg =
        err?.response?.data?.error ||
        err?.message ||
        "Failed to start DSA interview session";
      setListeningState("muted");
      stopSession({ statusAfter: `Error: ${msg}` });
    } finally {
      startRequestPendingRef.current = false;
      setStartingSession(false);
    }
  };

  const listeningConfig =
    voiceStartup.error ? { label: "Voice could not start - see recovery options", border: "#b42318", background: "#fef2f2", text: "#991b1b" } :
    LISTENING_STATE_CONFIG[listeningState] || LISTENING_STATE_CONFIG.muted;
  const livePanelStyle = {
    "--oral-status-border": listeningConfig.border,
    "--oral-status-bg": listeningConfig.background,
    "--oral-status-text": listeningConfig.text,
  };
  const statusCardStyle = {
    ...cardStyle,
    border: `1px solid ${listeningConfig.border}`,
    background: listeningConfig.background,
  };

  if (checkingPublicSession) return <div style={{ padding: 24 }}>Checking your account…</div>;
  if (publicSessionError) return <div style={{ padding: 24 }}><p>{publicSessionError}</p><button type="button" onClick={checkPublicSession}>Retry</button></div>;

  return (
    <div style={{ maxWidth: 980, margin: "0 auto", padding: 16, color: "#111827" }}>
      <h2 style={{ marginBottom: 8 }}>DSA Interview Practice</h2>
      <p style={{ marginTop: 0, color: "#4b5563" }}>
        Choose a DSA interview practice set. Each set contains a few interview
        questions and will generate one combined report.
      </p>
      <PublicAccountSummary user={publicUser} balances={publicBalances} onLogout={handlePublicLogout} loggingOut={loggingOut} />
      <div
        style={{
          ...cardStyle,
          marginBottom: 16,
          background: "#fffdf5",
          border: "1px solid #f59e0b",
          color: "#78350f",
        }}
      >
        The chatbot works best with headphones and a good microphone in a quiet environment. Make sure to allow microphone access when prompted.
        After you finish speaking, the interviewer response may take about 1 to 3
        seconds. It can take a little longer if your answer was longer, so please
        wait a moment before speaking again.
      </div>

      <div style={{ display: "grid", gap: 16 }}>
        <div style={cardStyle}>
          <h3 style={{ marginTop: 0 }}>Session Setup</h3>

          <div style={{ display: "grid", gap: 12 }}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: 12,
              }}
            >
              <div>
                <label style={labelStyle}>Name</label>
                <input
                  style={inputStyle}
                  value={studentName}
                  onChange={(e) => setStudentName(e.target.value)}
                  placeholder="Enter your full name"
                  disabled={voiceStartupLocked || isConnected || realtimeCleanupInProgress}
                />
              </div>

              {!emailVerified ? <div>
                <label style={labelStyle}>Email address</label>
                <input
                  style={inputStyle}
                  value={studentRegNumber}
                  onChange={(e) => {
                    setStudentRegNumber(e.target.value);
                    setPublicEmailCode("");
                    setEmailCodeSent(false);
                    setEmailVerified(false);
                    setUserId("");
                    setEmailVerificationStatus("");
                    setTopicsData(null);
                    setTopicsError("");
                    setSelectedStandard(DSA_LEVEL_ID);
                    setSelectedTopicSetId("");
                    setSelectedTopicId("");
                  }}
                  placeholder="you@example.com"
                  disabled={voiceStartupLocked || isConnected || realtimeCleanupInProgress}
                />
              </div> : null}

              {!emailVerified ? <div>
                <label style={labelStyle}>Verification code</label>
                <input
                  style={inputStyle}
                  value={publicEmailCode}
                  onChange={(e) => setPublicEmailCode(e.target.value)}
                  placeholder="Enter verification code"
                  disabled={voiceStartupLocked || isConnected || realtimeCleanupInProgress}
                />
              </div> : null}
            </div>
            {!emailVerified ? <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
              <button
                type="button"
                style={secondaryButtonStyle}
                onClick={handleSendEmailCode}
                disabled={
                  sendingEmailCode ||
                  voiceStartupLocked ||
                  isConnected ||
                  realtimeCleanupInProgress
                }
              >
                {sendingEmailCode ? "Sending..." : "Send verification code"}
              </button>
              <button
                type="button"
                style={secondaryButtonStyle}
                onClick={handleVerifyEmailCode}
                disabled={
                  verifyingEmailCode ||
                  voiceStartupLocked ||
                  isConnected ||
                  realtimeCleanupInProgress ||
                  !emailCodeSent
                }
              >
                {verifyingEmailCode ? "Verifying..." : "Verify email"}
              </button>
              <div style={{ color: emailVerified ? "#166534" : "#6b7280", fontSize: 14 }}>
                {emailVerificationStatus || "Verify your email to unlock DSA practice sets."}
              </div>
            </div> : null}
            {!emailVerified ? <div>
              <a href="/public-dashboard-embed" target="_blank" rel="noopener noreferrer">View My Account</a>
            </div> : null}
            <div style={{ color: "#6b7280", fontSize: 14 }}>
              {emailVerified
                ? verificationStatus
                : "DSA practice set selection appears after email verification."}
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
                gap: 12,
              }}
            >
              <div style={{ display: "none" }}>
                <label style={labelStyle}>Language</label>
                <select
                  style={inputStyle}
                  value={selectedLanguageId || "english"}
                  onChange={(e) =>
                    setSelectedLanguageId(e.target.value || "english")
                  }
                  disabled={
                    loadingTopics ||
                    voiceStartupLocked ||
                    isConnected ||
                    realtimeCleanupInProgress ||
                    !emailVerified
                  }
                >
                  {(languages || []).map((language) => (
                    <option
                      key={getSafeLanguageId(language)}
                      value={getSafeLanguageId(language)}
                    >
                      {language.language_name ||
                        language.language_id ||
                        "English"}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={labelStyle}>Level</label>
                <select
                  style={inputStyle}
                  value={selectedStandard}
                  onChange={(e) => setSelectedStandard(e.target.value)}
                  disabled={
                    loadingTopics ||
                    voiceStartupLocked ||
                    isConnected ||
                    realtimeCleanupInProgress ||
                    !emailVerified
                  }
                >
                  {(standards || []).map((standard) => (
                    <option key={standard.standard_id} value={standard.standard_id}>
                      {standard.standard_name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={labelStyle}>Choose a practice set</label>
                <select
                  style={inputStyle}
                  value={selectedTopicSetId}
                  onChange={(e) => {
                    setSelectedTopicSetId(e.target.value);
                    setSelectedTopicId(e.target.value);
                  }}
                  disabled={
                    loadingTopics ||
                    voiceStartupLocked ||
                    isConnected ||
                    realtimeCleanupInProgress ||
                    !emailVerified
                  }
                >
                  {(topicSets || []).map((topicSet) => (
                    <option
                      key={topicSet.question_set_id}
                      value={topicSet.question_set_id}
                    >
                      {topicSet.question_set_name}
                    </option>
                  ))}
                </select>
              </div>

              {selectedTopicSet ? (
                <div
                  style={{
                    border: "1px solid #e5e7eb",
                    borderRadius: 8,
                    padding: 12,
                    background: "#f9fafb",
                  }}
                >
                  <div style={{ fontWeight: 700 }}>
                    {selectedTopicSet.question_set_name}
                  </div>
                  {selectedTopicSet.category_name ? (
                    <div style={{ marginTop: 4, color: "#4b5563" }}>
                      Category: {selectedTopicSet.category_name}
                    </div>
                  ) : null}
                  {getQuestionSetQuestionCount(selectedTopicSet) ? (
                    <div style={{ marginTop: 4, color: "#4b5563" }}>
                      {getQuestionSetQuestionCount(selectedTopicSet)} questions
                    </div>
                  ) : null}
                  {selectedTopicSet.estimated_duration ? (
                    <div style={{ marginTop: 4, color: "#4b5563" }}>
                      Estimated duration: {selectedTopicSet.estimated_duration}
                    </div>
                  ) : null}
                </div>
              ) : null}

              <div>
                <label style={labelStyle}>Thinking Time</label>
                <select
                  style={inputStyle}
                  value={selectedPauseDurationMode}
                  onChange={(e) => setSelectedPauseDurationMode(e.target.value)}
                  disabled={voiceStartupLocked || isConnected || realtimeCleanupInProgress}
                >
                  {PAUSE_DURATION_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <div style={{ marginTop: 6, color: "#6b7280", fontSize: 13, lineHeight: 1.45 }}>
                  How long the AI waits after you stop speaking before responding or moving to the next question. Choose Extended if you'd like more time to think before the AI continues.
                </div>
              </div>
            </div>

            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button
                type="button"
                style={secondaryButtonStyle}
                onClick={refreshSelectedTopicDisplay}
                disabled={
                  loadingTopics ||
                  !emailVerified ||
                  !selectedStandard ||
                  !selectedTopicSetId ||
                  realtimeCleanupInProgress
                }
              >
                Refresh Practice Set
              </button>
            </div>

            <div
              style={{
                background: "#f9fafb",
                border: "1px solid #e5e7eb",
                borderRadius: 8,
                padding: 12,
                fontSize: 14,
              }}
            >
              <VoiceStartupRecovery error={voiceStartup.error} onRetry={voiceStartup.retry} directPath="/dsa-interview-voice" toolName="DSA Interview" />
              <div><b>Status:</b> {sessionStatus}</div>
              <div><b>Session ID:</b> {sessionInfo.sessionId || "-"}</div>
              <div><b>Candidate:</b> {studentName.trim() || "-"}</div>
              <div><b>Email:</b> {studentRegNumber.trim() || "-"}</div>
              <div><b>Email verified:</b> {emailVerified ? "Yes" : "No"}</div>
              <div><b>User ID:</b> {userId || "-"}</div>
              <div>
                <b>Practice Set:</b>{" "}
                {selectedTopicSet?.question_set_name || sessionInfo.topicSetId || "Default Topics"}
              </div>
              <div>
                <b>Current Interview Question:</b>{" "}
                {sessionInfo.questionCount
                  ? `${sessionInfo.currentQuestionIndex || "-"} of ${sessionInfo.questionCount}`
                  : sessionInfo.currentQuestionIndex || "-"}
              </div>
              <div><b>Report:</b> {reportStatus || (reportUrl ? "Ready" : "-")}</div>
              {ORAL_DEBUG ? (
                <>
                  <div><b>Realtime:</b> {isConnected ? "Connected" : "Not connected"}</div>
                  <div>
                    <b>studentReadyToAnswer:</b>{" "}
                    {studentReadyToAnswerRef.current ? "true" : "false"}
                  </div>
                  <div><b>readyButtonPressedAt:</b> {readyButtonPressedAt || "-"}</div>
                  <div>
                    <b>ignoredTranscriptBeforeReady:</b> {ignoredTranscriptBeforeReady}
                  </div>
                </>
              ) : null}

              <div
                style={{
                  marginTop: 10,
                  padding: 12,
                  borderRadius: 8,
                  background: listeningConfig.background,
                  color: listeningConfig.text,
                  border: `1px solid ${listeningConfig.border}`,
                  fontWeight: 700,
                  textAlign: "center",
                }}
              >
                {listeningConfig.label}
              </div>
              <div
                style={{
                  marginTop: 6,
                  color: listeningConfig.text,
                  fontSize: 13,
                  textAlign: "center",
                }}
              >
                {studentCanRespond
                  ? "Speak clearly now."
                  : "Take your time. Tap Ready to Answer when prepared."}
              </div>
            </div>

            {reportUrl && (
              <div>
                <button
                  type="button"
                  style={secondaryButtonStyle}
                  onClick={() =>
                    window.open(reportUrl, "_blank", "noopener,noreferrer")
                  }
                >
                Open DSA Report
                </button>
              </div>
            )}

            {loadingTopics && <div>Loading practice sets...</div>}
            {!!topicsError && (
              <div style={{ color: "#b42318" }}>Failed to load practice sets: {topicsError}</div>
            )}
          </div>
        </div>

        <div className="oral-live-panel" style={livePanelStyle}>
          <div className="oral-status-card" style={statusCardStyle}>
            <div className="oral-status-banner">{listeningConfig.label}</div>
          </div>

        <div className="oral-stimulus-panel" style={cardStyle}>
          <h3 style={{ marginTop: 0 }}>Interview Question</h3>

          {hasStimulusMedia && stimulus.mediaType === "image" ? (
            <div
              style={{
                minHeight: 520,
                maxHeight: 760,
                display: "grid",
                placeItems: "center",
                borderRadius: 10,
                border: "1px solid #e5e7eb",
                background: "#f3f4f6",
                overflow: "hidden",
                padding: 12,
              }}
            >
              <img
                src={stimulus.displayUrl}
                alt={selectedTopic?.topic_title || "Interview stimulus"}
                style={{
                  width: "100%",
                  height: "100%",
                  maxHeight: 736,
                  objectFit: "contain",
                  borderRadius: 10,
                  background: "#f3f4f6",
                }}
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
            </div>
          ) : hasStimulusMedia && stimulus.mediaType === "video" ? (
            <div
              style={{
                minHeight: 420,
                display: "grid",
                placeItems: "center",
                borderRadius: 10,
                border: "1px solid #e5e7eb",
                background: "#111827",
                overflow: "hidden",
                padding: 12,
              }}
            >
              <iframe
                src={stimulus.displayUrl}
                title={selectedTopic?.topic_title || "Video interview stimulus"}
                style={{
                  width: "100%",
                  aspectRatio: "16 / 9",
                  border: "none",
                  borderRadius: 10,
                  background: "#000",
                }}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            </div>
          ) : null}

          <div style={{ marginTop: hasStimulusMedia ? 16 : 0 }}>
            <div style={{ fontWeight: 700, fontSize: 22 }}>
              {selectedTopic?.topic_title || sessionInfo.topicTitle || "-"}
            </div>
            <div style={{ marginTop: 8, color: "#4b5563", lineHeight: 1.6 }}>
              {stimulus.description ||
                selectedTopic?.picture_stimulus ||
                sessionInfo.pictureStimulus ||
                "The selected interview question will appear here after session start."}
            </div>
          </div>

          <div style={{ marginTop: 20 }}>
            <div style={{ fontWeight: 700, marginBottom: 8, fontSize: 16 }}>
              Interview Question Flow
            </div>
            <ol style={{ margin: 0, paddingLeft: 24, color: "#374151", lineHeight: 1.8 }}>
              {orderedDisplayQuestions.length > 0 ? (
                orderedDisplayQuestions.map((question) => (
                  <li key={question.key}>{question.text}</li>
                ))
              ) : (
                <li>-</li>
              )}
            </ol>
          </div>

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 20 }}>
            <button
              type="button"
              style={primaryButtonStyle}
              onClick={startVoiceSession}
              disabled={
                loadingTopics ||
                voiceStartupLocked ||
                isConnected ||
                realtimeCleanupInProgress ||
                !emailVerified ||
                !selectedStandard ||
                !selectedTopicSetId
              }
            >
              {(startingSession || voiceStartup.busy) ? "Starting..." : "Start DSA Interview Practice"}
            </button>

            {isConnected &&
            waitingForReadyPress &&
            sessionInfo.status !== "completed" &&
            !studentCanRespond ? (
              <button
                type="button"
                style={primaryButtonStyle}
                onClick={handleReadyToAnswer}
              >
                I'm Ready to Answer
              </button>
            ) : null}

            <button
              type="button"
              style={dangerButtonStyle}
              onClick={stopSession}
              disabled={!isConnected || realtimeCleanupInProgress}
            >
              End Session
            </button>
            <button
              type="button"
              style={secondaryButtonStyle}
              onClick={refreshSelectedTopicDisplay}
              disabled={
                loadingTopics ||
                !emailVerified ||
                !selectedStandard ||
                !selectedTopicSetId ||
                realtimeCleanupInProgress
              }
            >
              Refresh Practice Set
            </button>
          </div>
        </div>
      <div className="oral-log-card" style={cardStyle}>
        <h3 style={{ marginTop: 0 }}>Session Log</h3>

        {eventLog.length === 0 ? (
          <div style={{ color: "#6b7280" }}>
            No session events yet.
          </div>
        ) : (
          <div style={{ display: "grid", gap: 10 }}>
            {eventLog.map((item) => (
              <div
                key={item.id}
                style={{
                  border: "1px solid #e5e7eb",
                  borderRadius: 10,
                  padding: 12,
                  background:
                    item.speaker === "Interviewer"
                      ? "#eef2ff"
                      : item.speaker === "Student"
                      ? "#f9fafb"
                      : "#fff7ed",
                }}
              >
                <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 4 }}>
                  <b>{item.speaker}</b> · {item.at}
                </div>
                <div style={{ whiteSpace: "pre-wrap" }}>
                  {item.text.startsWith("http://") ||
                  item.text.startsWith("https://") ? (
                    <a
                      href={item.text}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {item.text}
                    </a>
                  ) : (
                    item.text
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
        </div>
      </div>

    </div>
  );
}

export default DsaInterviewVoicePage;
